import React, { useState, useEffect, useCallback, useMemo, useDeferredValue } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Building2,
  Users,
  Plus,
  Search,
  List as ListIcon,
  LayoutDashboard,
  Calendar,
  MapPin,
  ArrowUpDown,
  Clock,
  ExternalLink,
  ChevronRight,
  ChevronDown,
  MoreVertical,
  Eye,
  CheckCircle2,
  AlertCircle,
  IndianRupee,
  GraduationCap as GradCap,
  Globe,
  Mail,
  Phone,
  BarChart2,
  ArrowUpRight,
  Target,
  Settings,
  Activity,
  RefreshCw,
  ArrowLeft,
  Download,
  ArrowUp,
  ArrowDown,
  FileText,
  SlidersHorizontal,
  Sparkles,
  Loader2,
  Wand2,
  X,
  Upload,
  Save,
  Edit,
  Trash2,
  Zap,
  Award,
  ArrowRight,
  Check
} from 'lucide-react';
import { IconCalendarEvent, IconBuilding, IconBriefcase } from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
  Sector
} from 'recharts';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';
import api, { getFileUrl } from '../../utils/api';
import { matchRoleByTitle, roleToStructuredSkills, findRoleByExactTitle } from '../../data/roleSkillsLibrary';
import { fetchRoleSkillsByTitle, dbRoleToStructuredSkills } from '../../utils/roleSkills';
import { searchLocations, isKnownLocation } from '../../data/jobLocations';
import ProjectDomainPicker from '../../components/Common/ProjectDomainPicker';
import CourseSkillPicker from '../../components/Common/CourseSkillPicker';
import { showModerationFeedback } from '../../utils/moderationFeedback';
import { validateSalaryRange, validateMustHaveSkills, validateUpcomingDate } from '../../utils/jobFormRules';
import { useAuth } from '../../context/AuthContext';
import { useDegrees } from '../../context/DegreeContext';
import { useTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import useGlobalSearch from '../../hooks/useGlobalSearch';
import CollegeRecruiter from './CollegeRecruiter';
import StudentProgress from './StudentProgress';
import PipelineBoard from '../../components/Recruiter/PipelineBoard';
import ApplicantPackModal from '../../components/Recruiter/ApplicantPackModal';
import JobDetailsModal from '../../components/Recruiter/JobDetailsModal';

/**
 * A degree's `specialization` is `Mixed` in the schema, so it can arrive as a
 * string or an array of branches. Collapse to display text before it reaches
 * anything doing string work.
 */
const asText = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean).join(', ');
  if (typeof v === 'string') return v;
  return '';
};

/** An empty array is truthy, so `a || b` would pick `[]` over a populated list. */
const firstNonEmpty = (...lists) => lists.find((l) => Array.isArray(l) && l.length > 0) || [];

/** Degree / branch as text, whichever shape the record happens to use. */
const studentDegreeText = (s) => {
  const d = s?.degree;
  if (d && typeof d === 'object') return asText(d.abbreviation) || asText(d.fullName);
  return asText(d) || asText(s?.academic?.degreeGroup) || asText(s?.department?.degreeFullName);
};

const studentBranchText = (s) => {
  const d = s?.degree;
  if (d && typeof d === 'object' && d.specialization) return asText(d.specialization);
  return asText(s?.academic?.specialisation)
    || asText(typeof s?.department === 'object' ? s?.department?.specialization : s?.department);
};

const SAMPLE_JD = `Software Engineer — Full Stack (Campus Hire)

Location: Mumbai (Hybrid). Minimum CGPA: 7.0

We are hiring graduates with strong fundamentals in JavaScript, React, Node.js, MongoDB, and SQL.
You will build REST APIs, work with Git, and collaborate in Agile teams.

Nice to have: TypeScript, Docker, AWS, Communication, Problem Solving.`;

const matchScoreBarColor = (pct) => {
  if (pct >= 80) return 'bg-[var(--placement-primary-light)]0';
  if (pct >= 50) return 'bg-amber-500';
  return 'bg-rose-500';
};

const matchScoreTextColor = (pct) => {
  if (pct >= 80) return 'text-[var(--placement-primary)] dark:text-[var(--placement-primary)]';
  if (pct >= 50) return 'text-amber-600 dark:text-amber-400';
  return 'text-rose-600 dark:text-rose-400';
};

const formatCGPA = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : '—';
};

// Consistent avatar gradient derived from a name
const avatarGradient = (name) => {
  if (!name) return 'linear-gradient(135deg, var(--placement-primary), #3ab5e6)';
  const hash = name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
  const hues = [224, 262, 338, 142, 24, 186, 343];
  const primaryHue = hues[hash % hues.length];
  const secondaryHue = (primaryHue + 40) % 360;
  return `linear-gradient(135deg, hsl(${primaryHue}, 82%, 58%), hsl(${secondaryHue}, 78%, 44%))`;
};

const initialsOf = (name) => (name
  ? name.trim().split(/\s+/).map(n => n[0]).join('').slice(0, 2).toUpperCase()
  : '—');

// Normalize an application status into a display bucket + styling
const getAppStatusMeta = (rawStatus) => {
  const s = (rawStatus || 'Applied').toLowerCase();
  if (['placed', 'selected', 'offered', 'hired'].includes(s)) {
    return { label: rawStatus || 'Placed', key: 'Placed', dot: 'bg-emerald-500', text: 'text-emerald-700 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-[#1a3884]/20', border: 'border-emerald-200 dark:border-emerald-900/40' };
  }
  if (['rejected', 'declined'].includes(s)) {
    return { label: rawStatus || 'Rejected', key: 'Rejected', dot: 'bg-rose-500', text: 'text-rose-700 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-950/30', border: 'border-rose-200 dark:border-rose-900/40' };
  }
  if (s === 'shortlisted') {
    return { label: 'Shortlisted', key: 'Shortlisted', dot: 'bg-[var(--placement-primary)]', text: 'text-[var(--placement-primary)] dark:text-[#9bb8ff]', bg: 'bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary)]/20', border: 'border-[var(--placement-primary-border)] dark:border-[var(--placement-primary-border)]/40' };
  }
  if (s === 'under review') {
    return { label: 'Under Review', key: 'Under Review', dot: 'bg-sky-500', text: 'text-sky-700 dark:text-sky-400', bg: 'bg-sky-50 dark:bg-sky-950/30', border: 'border-sky-200 dark:border-sky-900/40' };
  }
  return { label: rawStatus || 'Applied', key: 'Applied', dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30', border: 'border-amber-200 dark:border-amber-900/40' };
};

const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius - 3}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        style={{ filter: 'drop-shadow(0px 8px 16px rgba(0,0,0,0.15))' }}
      />
    </g>
  );
};

const CustomTooltip = ({ active, payload, isDark }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/90 dark:bg-[#002147]/95 backdrop-blur-md border border-gray-100 dark:border-[var(--placement-primary-border)]/40 px-3.5 py-2.5 rounded-xl shadow-2xl transition-all duration-300">
        <p className="text-xs font-black text-gray-900 dark:text-white leading-relaxed">{payload[0].name}</p>
        <p className="text-xs font-bold text-[var(--placement-primary)] mt-1 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--placement-primary)] animate-ping" />
          {payload[0].value} {payload[0].value === 1 ? 'student' : 'students'}
        </p>
      </div>
    );
  }
  return null;
};

const CompanyCard = ({ company, jobs, applications, onEdit, onDelete, onView, isDark }) => {
  const [imgError, setImgError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [branchesExpanded, setBranchesExpanded] = useState(false);
  const menuRef = React.useRef(null);

  // Close menu on outside click
  React.useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const initials = company.name
    ? company.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : 'CO';

  const getGradient = (name) => {
    const hash = name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    const hues = [224, 262, 338, 142, 24, 186, 343];
    const primaryHue = hues[hash % hues.length];
    const secondaryHue = (primaryHue + 40) % 360;
    return `linear-gradient(135deg, hsl(${primaryHue}, 85%, 60%), hsl(${secondaryHue}, 80%, 45%))`;
  };

  const cardGradient = getGradient(company.name);

  return (
    <div onClick={() => onView && onView(company)} className="cursor-pointer bg-white dark:bg-[#002147] rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.02)] dark:shadow-[0_8px_30px_rgb(0,0,0,0.15)] border border-gray-100/80 dark:border-[var(--placement-primary-border)]/35 hover:shadow-lg transition-all duration-300 flex flex-col justify-between group relative max-w-[280px] w-full mx-auto h-full">
      <div className="relative z-10 flex flex-col h-full items-center text-center w-full">
        {/* 3-dot dropdown */}
        <div className="absolute top-0 right-0" ref={menuRef}>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen(prev => !prev); }}
            className="p-1.5 text-gray-400 hover:text-gray-655 dark:hover:text-white rounded-lg hover:bg-gray-50 dark:hover:bg-[#001f40]/55 transition-all"
          >
            <MoreVertical className="w-4 h-4" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-[#002147] border border-gray-100 dark:border-[var(--placement-primary-border)]/40 rounded-xl shadow-xl z-50 py-1.5 overflow-hidden text-left">
              <button
                onClick={() => { setMenuOpen(false); onEdit(company); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-[var(--placement-primary-hover)]/20 transition-colors text-left"
              >
                <Edit className="w-4 h-4 text-[var(--placement-primary)]" />
                Edit
              </button>
              <button
                onClick={() => { setMenuOpen(false); onDelete(company._id); }}
                className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors text-left"
              >
                <X className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}
        </div>

        {/* Centered Logo */}
        <div className="w-16 h-16 rounded-2xl bg-white dark:bg-[#002147] border border-gray-150 dark:border-[var(--placement-primary-border)]/30 flex items-center justify-center overflow-hidden shrink-0 shadow-[0_4px_12px_-2px_rgba(0,0,0,0.05)] mb-4 mt-2">
          {company.logo && !imgError ? (
            <img
              src={company.logo}
              alt={company.name}
              onError={() => setImgError(true)}
              className="w-full h-full object-contain rounded-xl"
            />
          ) : (
            <div
              className="w-full h-full rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-sm"
              style={{ background: cardGradient }}
            >
              {initials}
            </div>
          )}
        </div>

        {/* Centered Company Name */}
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-1 w-full px-1">
          {company.name}
        </h2>

        {/* Compact Website */}
        {company.website && (
          <a
            href={company.website}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center justify-center gap-1 text-xs font-semibold text-[var(--placement-primary)] hover:underline truncate w-full px-2"
          >
            <Globe className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{company.website.replace(/^https?:\/\//, '')}</span>
          </a>
        )}

        {/* Company Description */}
        {company.description && (
          <div className="mt-3 w-full px-2 border-t border-slate-100 dark:border-slate-800 pt-3">
            <p className="text-[11px] text-slate-500 dark:text-slate-400 line-clamp-3 leading-relaxed text-center">
              {company.description}
            </p>
          </div>
        )}
      </div>

      {/* Branches Accordion */}
      {company.branches && company.branches.length > 0 && (
        <div className="mt-4 border-t border-gray-150 dark:border-[#1a3884]/30 w-full pt-1 relative z-20">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setBranchesExpanded(!branchesExpanded);
            }}
            className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-[#001f40]/50 transition-colors rounded-xl"
          >
            <div className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-[var(--placement-primary)] dark:text-blue-400" />
              <span className="text-xs font-bold text-gray-700 dark:text-gray-200">
                Branches ({company.branches.length})
              </span>
            </div>
            <ArrowUpDown className="w-3 h-3 text-gray-400" />
          </button>

          {branchesExpanded && (
            <div className="p-3 bg-gray-50/50 dark:bg-[#001f40]/30 space-y-2.5 max-h-48 overflow-y-auto custom-scrollbar rounded-b-2xl">
              {company.branches.map((branch, idx) => (
                <div key={idx} className="p-3 rounded-xl bg-white dark:bg-[#002147] border border-gray-150 dark:border-[#1a3884]/20 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold text-gray-800 dark:text-gray-200">{branch.branchName}</span>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md uppercase tracking-wider ${branch.status === 'active' ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30' : 'bg-rose-50 text-rose-600 dark:bg-rose-950/30'}`}>
                      {branch.status || 'active'}
                    </span>
                  </div>
                  {(branch.city || branch.state) && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 line-clamp-1 mb-1">
                      {branch.city}{branch.city && branch.state ? ', ' : ''}{branch.state}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


const JobCard = ({ job, onEdit, onDelete, onToggleStatus, isDark }) => {
  const [imgError, setImgError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const initials = job.company?.name
    ? job.company.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : 'JO';

  const getGradient = (name) => {
    if (!name) return 'linear-gradient(135deg, var(--placement-primary), #3ab5e6)';
    const hash = name.split('').reduce((acc, char) => char.charCodeAt(0) + acc, 0);
    const hues = [224, 262, 338, 142, 24, 186, 343];
    const primaryHue = hues[hash % hues.length];
    const secondaryHue = (primaryHue + 40) % 360;
    return `linear-gradient(135deg, hsl(${primaryHue}, 85%, 60%), hsl(${secondaryHue}, 80%, 45%))`;
  };

  const logoGradient = getGradient(job.company?.name);

  const statusConfig = {
    open: { label: 'ACTIVE', bg: 'bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary-light)]', text: 'text-[var(--placement-primary)] dark:text-[var(--placement-primary)]', border: 'border-[var(--placement-primary-border)] dark:border-[var(--placement-primary-border)]', dot: 'bg-[var(--placement-primary-light)]0 animate-pulse' },
    active: { label: 'ACTIVE', bg: 'bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary-light)]', text: 'text-[var(--placement-primary)] dark:text-[var(--placement-primary)]', border: 'border-[var(--placement-primary-border)] dark:border-[var(--placement-primary-border)]', dot: 'bg-[var(--placement-primary-light)]0 animate-pulse' },
    upcoming: { label: 'UPCOMING', bg: 'bg-amber-50 dark:bg-amber-950/30', text: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200 dark:border-amber-900/30', dot: 'bg-amber-500' },
    closed: { label: 'INACTIVE', bg: 'bg-rose-50 dark:bg-rose-950/30', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-200 dark:border-rose-900/30', dot: 'bg-rose-500' },
    inactive: { label: 'INACTIVE', bg: 'bg-rose-50 dark:bg-rose-950/30', text: 'text-rose-600 dark:text-rose-400', border: 'border-rose-200 dark:border-rose-900/30', dot: 'bg-rose-500' },
  };
  const st = statusConfig[job.status] || statusConfig.inactive;

  return (
    <div className="bg-white dark:bg-[#002147] rounded-2xl p-5 shadow-sm hover:shadow-md border border-gray-100 dark:border-[var(--placement-primary-border)]/30 transition-all duration-300 flex flex-col gap-3 group">
      {/* Top row: logo + title + status + menu */}
      <div className="flex items-start gap-3">
        {/* Logo */}
        <div className="w-11 h-11 rounded-xl bg-gray-50 dark:bg-slate-800 border border-gray-100 dark:border-slate-700 flex items-center justify-center p-1.5 shrink-0 overflow-hidden">
          {job.company?.logo && !imgError ? (
            <img
              src={job.company.logo}
              alt={job.company?.name || 'Company'}
              onError={() => setImgError(true)}
              className="w-full h-full object-contain"
            />
          ) : (
            <div
              className="w-full h-full rounded-lg flex items-center justify-center text-white font-black text-xs"
              style={{ background: logoGradient }}
            >
              {initials}
            </div>
          )}
        </div>

        {/* Title + company + tags */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight group-hover:text-[var(--placement-primary)] dark:group-hover:text-[var(--placement-primary)] transition-colors">
              {job.title}
            </h3>
            <div className="flex items-center gap-1.5 shrink-0">
              {/* Status badge */}
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${st.bg} ${st.text} ${st.border}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />
                {st.label}
              </span>
              {/* 3-dot menu */}
              <div className="relative" ref={menuRef}>
                <button
                  type="button"
                  onClick={() => setMenuOpen(p => !p)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <MoreVertical size={15} />
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-1.5 w-36 bg-white dark:bg-[#001f40] border border-gray-150 dark:border-[var(--placement-primary-border)]/40 rounded-xl shadow-lg py-1 z-20">
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); onToggleStatus(job); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 text-left font-medium"
                    >
                      <Zap size={13} className={['open', 'active'].includes(job.status) ? "text-amber-500" : "text-emerald-500"} />
                      {['open', 'active'].includes(job.status) ? 'Mark as Inactive' : 'Mark as Active'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); onEdit(job); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800 text-left font-medium border-t border-gray-100 dark:border-slate-800"
                    >
                      <Edit size={13} className="text-[var(--placement-primary)]" />
                      Edit Drive
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMenuOpen(false); onDelete(job._id); }}
                      className="flex items-center gap-2 w-full px-3 py-2 text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-950/20 text-left font-medium border-t border-gray-100 dark:border-slate-800"
                    >
                      <Trash2 size={13} className="text-red-500" />
                      Delete Drive
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Company name + pills */}
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <span className="text-xs text-gray-500 dark:text-slate-400 font-semibold">{job.company?.name}</span>
            {job.jobType && (
              <>
                <span className="text-[10px] text-gray-300 dark:text-slate-600">•</span>
                <span className="px-1.5 py-0.5 bg-[var(--placement-primary)]/8 dark:bg-[var(--placement-primary)]/25 text-[var(--placement-primary)] dark:text-[var(--placement-primary)] text-[10px] font-semibold rounded-md border border-[var(--placement-primary-border)]/10 dark:border-[var(--placement-primary-border)]/30">{job.jobType}</span>
              </>
            )}
            {job.workMode && (
              <>
                <span className="text-[10px] text-gray-300 dark:text-slate-600">•</span>
                <span className="px-1.5 py-0.5 bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary-light)] text-[var(--placement-primary)] dark:text-[var(--placement-primary)] text-[10px] font-semibold rounded-md border border-[var(--placement-primary-border)] dark:border-[var(--placement-primary-border)]">{job.workMode}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      {job.description && (
        <p className="text-xs text-gray-500 dark:text-slate-400 line-clamp-2 leading-relaxed">{job.description}</p>
      )}

      {/* Info list */}
      <div className="space-y-1.5 border-t border-gray-100 dark:border-slate-800/60 pt-3 text-xs text-gray-500 dark:text-slate-400">
        {job.location && (
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span>{job.location}</span>
          </div>
        )}
        {job.salaryPackage && (
          <div className="flex items-center gap-2">
            <IndianRupee className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span>{job.salaryPackage}</span>
          </div>
        )}
        {job.eligibility?.minCGPA != null && job.eligibility.minCGPA > 0 && (
          <div className="flex items-center gap-2">
            <GradCap className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span>Min CGPA: {job.eligibility.minCGPA}</span>
          </div>
        )}
        {job.eligibility?.noBacklog && (
          <div className="flex items-center gap-2">
            <AlertCircle className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span>No Active Backlogs</span>
          </div>
        )}
        {(job.eligibility?.allowedDegrees?.length > 0 || job.eligibility?.allowedBranches?.length > 0) && (
          <div className="flex items-start gap-2">
            <GradCap className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" />
            <div className="flex flex-col gap-0.5">
              {job.eligibility?.allowedDegrees?.length > 0 && (
                <span className="line-clamp-1" title={job.eligibility.allowedDegrees.join(', ')}>
                  Degrees: {job.eligibility.allowedDegrees.join(', ')}
                </span>
              )}
              {job.eligibility?.allowedBranches?.length > 0 && (
                <span className="line-clamp-1" title={job.eligibility.allowedBranches.join(', ')}>
                  Branches: {job.eligibility.allowedBranches.join(', ')}
                </span>
              )}
            </div>
          </div>
        )}
        {job.deadline && (
          <div className="flex items-center gap-2">
            <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
            <span>Apply by: {new Date(job.deadline).toLocaleDateString()}</span>
          </div>
        )}
      </div>


    </div>
  );
};

const JobFairCard = ({ fair, onEdit, onDelete, isDark }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    if (menuOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const statusConfig = {
    active: { label: 'Live', dot: 'bg-emerald-500 animate-pulse' },
    draft: { label: 'Draft', dot: 'bg-slate-400' },
    completed: { label: 'Completed', dot: 'bg-blue-500' },
    cancelled: { label: 'Cancelled', dot: 'bg-rose-500' },
  };
  const st = statusConfig[fair.status] || statusConfig.draft;
  const isSmaart = fair.label?.toLowerCase() === 'smaart job fair';

  return (
    <div className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition-all duration-200 hover:border-[#1a3884]/35 hover:shadow-[0_4px_20px_-4px_rgba(13,31,78,0.14)] dark:border-[#1a3884]/30 dark:bg-[#002147]">

      {/* Banner — the navy gradient sits underneath permanently, so cards without
          a bannerImage (or with a dead URL) keep the same height and branding. */}
      <div className="relative h-32 w-full shrink-0 overflow-hidden bg-gradient-to-br from-[#1a3884] to-[#00152E]">
        <div className="absolute inset-0 flex items-center justify-center">
          <Calendar className="h-8 w-8 text-white/25" />
        </div>
        {fair.bannerImage && (
          <img
            src={fair.bannerImage.startsWith('http') ? fair.bannerImage : `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${fair.bannerImage}`}
            alt={fair.title}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
          />
        )}
        {/* Scrim keeps the badges legible over any photo */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/20" />
        <div className="absolute inset-x-3 top-3 flex items-start justify-between gap-2">
          <span className={`rounded-md px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] backdrop-blur-sm ${
            isSmaart ? 'bg-white/95 text-[#0d1f4e]' : 'bg-black/35 text-white ring-1 ring-white/30'
          }`}>
            {isSmaart ? 'SMAART' : 'College'}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-white/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.05em] text-[#0d1f4e] backdrop-blur-sm">
            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} />
            {st.label}
          </span>
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4">
        {/* Title + menu */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-[15px] font-semibold leading-tight tracking-[-0.01em] text-slate-900 dark:text-white" title={fair.title}>
              {fair.title}
            </h3>
            {fair.description && (
              <p className="mt-0.5 line-clamp-1 text-[12px] text-slate-500 dark:text-slate-400">{fair.description}</p>
            )}
          </div>
          <div className="relative shrink-0" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen(p => !p)}
              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <MoreVertical size={14} />
            </button>
            {menuOpen && (
              <div className={`absolute right-0 z-30 mt-1.5 w-36 animate-fade-in rounded-xl border p-1 shadow-xl ${
                isDark ? 'bg-[#00152e] border-slate-700/50' : 'bg-white border-slate-100'
              }`}>
                <button
                  onClick={() => { onEdit(fair); setMenuOpen(false); }}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:text-slate-200 dark:hover:bg-[#1a3884]/20"
                >
                  <Edit size={12} className="text-[#1a3884] dark:text-[#4f7dff]" />
                  Edit Details
                </button>
                <button
                  onClick={() => { onDelete(fair._id); setMenuOpen(false); }}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-rose-600 transition-colors hover:bg-rose-50 dark:hover:bg-rose-950/20"
                >
                  <Trash2 size={12} className="text-red-500" />
                  Delete Fair
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Info grid */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2 border-t border-slate-100 pt-3 text-[12px] text-slate-600 dark:border-slate-800/60 dark:text-slate-300">
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate">{new Date(fair.startDate).toLocaleDateString()} - {new Date(fair.endDate).toLocaleDateString()}</span>
          </div>
          {fair.location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span className="truncate">{fair.location}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <Building2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span>{fair.companies?.length || 0} {(fair.companies?.length || 0) === 1 ? 'Company' : 'Companies'}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span>{fair.registeredStudents?.length || 0} Registered</span>
          </div>
          {fair.registrationDeadline && (
            <div className="col-span-2 flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
              <Clock className="h-3.5 w-3.5 shrink-0 text-slate-400" />
              <span>RSVP by {new Date(fair.registrationDeadline).toLocaleDateString()}</span>
            </div>
          )}
          <div
            className="col-span-2 flex items-center gap-1.5 text-slate-500 dark:text-slate-400"
            title={
              fair.eligibility?.allowedBranches?.length > 0
                ? 'Specialisations: ' + fair.eligibility.allowedBranches.join(', ')
                : undefined
            }
          >
            <GradCap className="h-3.5 w-3.5 shrink-0 text-slate-400" />
            <span className="truncate">
              {fair.eligibility?.allowedDegrees?.length > 0
                ? fair.eligibility.allowedDegrees.join(', ') +
                  (fair.eligibility?.allowedBranches?.length > 0
                    ? ` · ${fair.eligibility.allowedBranches.length} specialisations`
                    : '')
                : 'Open to all degrees'}
            </span>
          </div>
        </div>

        {/* Action */}
        <button
          onClick={() => window.location.href = `/college/job-fairs/${fair._id}`}
          className={`mt-auto flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-[13px] font-semibold transition-colors ${
            isSmaart
              ? 'border border-gray-200 bg-white text-[#1a3884] hover:border-[#1a3884]/40 hover:bg-gray-50 dark:border-[#1a3884]/30 dark:bg-[#001430] dark:text-[#4f7dff] dark:hover:bg-[#1a3884]/10'
              : 'bg-[#0d1f4e] text-white hover:bg-[#1a3884]'
          }`}
        >
          {isSmaart ? 'View Details' : 'Run the Day'}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

const JDAnalyzer = ({ isDark }) => {
  const [formData, setFormData] = useState({
    jdText: '',
    minCGPA: '',
    skills: '',
    location: ''
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState([]);
  const [requiredSkills, setRequiredSkills] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(null);

  const handleAnalyze = async (e) => {
    e.preventDefault();
    if (!formData.jdText.trim()) {
      toast.error('Paste JD text before analyzing candidates');
      return;
    }

    try {
      setAnalyzing(true);
      const payload = {
        jdText: formData.jdText,
        minCGPA: formData.minCGPA,
        skills: formData.skills.split(',').map((skill) => skill.trim()).filter(Boolean),
        location: formData.location
      };
      const response = await api.post('/placements/jd-analyzer', payload);
      if (response.data.success) {
        setResults(response.data.data || []);
        setRequiredSkills(response.data.meta?.requiredSkills || []);
        setAppliedFilters(response.data.meta?.filters || null);
        const count = response.data.data?.length || 0;
        toast.success(count > 0 ? `Top ${count} candidates ranked by skill match` : 'No students matched your filters');
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Unable to analyze JD');
    } finally {
      setAnalyzing(false);
    }
  };

  const topScore = results[0]?.skillMatchPercentage || 0;

  return (
    <div className="space-y-6">
      <div className={`relative overflow-hidden rounded-[1.5rem] border shadow-xl ${isDark
        ? 'bg-[#002147] border-[var(--placement-primary-border)]/35 shadow-[#001030]/30'
        : 'bg-white border-slate-100 shadow-slate-200/60'
        }`}>
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#1a3884] via-[var(--placement-primary)] to-[#3ab5e6]" />
        <div className="grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr]">
          <form onSubmit={handleAnalyze} className="p-6 lg:p-8 space-y-6 border-b xl:border-b-0 xl:border-r border-slate-100 dark:border-[var(--placement-primary-border)]/30">
            <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-5">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 rounded-full bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary)]/20 border border-[var(--placement-primary-border)] dark:border-[var(--placement-primary-border)]/35 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-[var(--placement-primary)] dark:text-[#9bb8ff]">
                  <Sparkles className="w-3.5 h-3.5" />
                  Smart Matching
                </div>
                <h2 className="mt-4 text-2xl font-black text-slate-950 dark:text-white tracking-tight">JD Analyzer</h2>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 font-medium leading-6">
                  Paste a job description, add optional filters, and shortlist students by skill match percentage.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-lg bg-[var(--placement-primary)] px-3 py-1.5 shrink-0 shadow-sm shadow-[var(--placement-primary-border)]/10">
                <span className="text-[9px] font-bold text-blue-100/80 uppercase tracking-wider">Best match</span>
                <span className="text-sm font-black text-white leading-none tabular-nums">{topScore}%</span>
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Job Description Text</label>
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, jdText: SAMPLE_JD }))}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-[var(--placement-primary-border)]/40 bg-white dark:bg-[#001f40] px-3 py-1.5 text-[11px] font-bold text-[var(--placement-primary)] dark:text-[#9bb8ff] hover:bg-[var(--placement-primary-light)] dark:hover:bg-[var(--placement-primary-hover)]/20 transition-colors"
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  Load sample JD
                </button>
              </div>
              <textarea
                value={formData.jdText}
                onChange={(e) => setFormData({ ...formData, jdText: e.target.value })}
                placeholder="Paste role responsibilities, required skills, eligibility, location, and hiring notes..."
                className="mt-2 w-full min-h-[220px] rounded-2xl border border-slate-200 dark:border-[var(--placement-primary-border)]/40 bg-slate-50/70 dark:bg-[#001f40] px-5 py-4 text-sm leading-6 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-[var(--placement-primary)]/35 focus:border-[var(--placement-primary)] focus:outline-none focus:ring-4 focus:ring-[var(--placement-primary-border)]/15 resize-y"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Minimum CGPA</label>
                <input
                  type="number"
                  min="0"
                  max="10"
                  step="0.1"
                  value={formData.minCGPA}
                  onChange={(e) => setFormData({ ...formData, minCGPA: e.target.value })}
                  placeholder="e.g. 7.0"
                  className="mt-2 w-full rounded-2xl border border-slate-200 dark:border-[var(--placement-primary-border)]/40 bg-white dark:bg-[#001f40] px-4 py-3 text-sm font-bold text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-[var(--placement-primary)]/35 focus:border-[var(--placement-primary)] focus:outline-none focus:ring-4 focus:ring-[var(--placement-primary-border)]/15"
                />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Skills Filter</label>
                <input
                  value={formData.skills}
                  onChange={(e) => setFormData({ ...formData, skills: e.target.value })}
                  placeholder="React, SQL, Python"
                  className="mt-2 w-full rounded-2xl border border-slate-200 dark:border-[var(--placement-primary-border)]/40 bg-white dark:bg-[#001f40] px-4 py-3 text-sm font-bold text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-[var(--placement-primary)]/35 focus:border-[var(--placement-primary)] focus:outline-none focus:ring-4 focus:ring-[var(--placement-primary-border)]/15"
                />
              </div>
              <div>
                <label className="text-[11px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Location</label>
                <input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Mumbai / Remote"
                  className="mt-2 w-full rounded-2xl border border-slate-200 dark:border-[var(--placement-primary-border)]/40 bg-white dark:bg-[#001f40] px-4 py-3 text-sm font-bold text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-[var(--placement-primary)]/35 focus:border-[var(--placement-primary)] focus:outline-none focus:ring-4 focus:ring-[var(--placement-primary-border)]/15"
                />
              </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5 rounded-2xl bg-slate-50 dark:bg-[#001f40]/70 border border-slate-100 dark:border-[var(--placement-primary-border)]/25 p-4">
              <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 font-semibold leading-5">
                <SlidersHorizontal className="w-4 h-4 text-[var(--placement-primary)]" />
                Filters apply before ranking; ranking is by skill match percentage.
              </div>
              <button
                type="submit"
                disabled={analyzing}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)] disabled:opacity-60 px-8 py-3 text-sm font-black text-white shadow-lg shadow-[var(--placement-primary-border)]/20 transition-all min-w-[180px]"
              >
                {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                {analyzing ? 'Analyzing...' : 'Analyze JD'}
              </button>
            </div>
          </form>

          <div className="p-6 lg:p-8 bg-slate-50/70 dark:bg-[#001f40]/45 space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="rounded-2xl border border-slate-100 dark:border-[var(--placement-primary-border)]/30 bg-white dark:bg-[#002147] p-4 shadow-sm">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Candidates</span>
                <div className="text-2xl font-black text-slate-950 dark:text-white mt-2">{results.length}</div>
              </div>
              <div className="rounded-2xl border border-slate-100 dark:border-[var(--placement-primary-border)]/30 bg-white dark:bg-[#002147] p-4 shadow-sm">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Skills Found</span>
                <div className="text-2xl font-black text-slate-950 dark:text-white mt-2">{requiredSkills.length}</div>
              </div>
              <div className="rounded-2xl border border-slate-100 dark:border-[var(--placement-primary-border)]/30 bg-white dark:bg-[#002147] p-4 shadow-sm">
                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Limit</span>
                <div className="text-2xl font-black text-slate-950 dark:text-white mt-2">20</div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-100 dark:border-[var(--placement-primary-border)]/30 bg-white dark:bg-[#002147] p-5 shadow-sm min-h-[170px]">
              <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Required Skills Detected</h3>
              <div className="mt-4 flex flex-wrap gap-2">
                {requiredSkills.length > 0 ? requiredSkills.map((skill) => (
                  <span key={skill} className="rounded-lg bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary)]/15 border border-[var(--placement-primary-border)] dark:border-[var(--placement-primary)]/25 px-3 py-1.5 text-xs font-bold text-[var(--placement-primary)] dark:text-[var(--placement-primary)]">
                    {skill}
                  </span>
                )) : (
                  <span className="text-sm text-slate-400 dark:text-slate-500">Skills appear here after analysis.</span>
                )}
              </div>
            </div>

            {appliedFilters && (
              <div className="rounded-2xl border border-slate-100 dark:border-[var(--placement-primary-border)]/30 bg-white dark:bg-[#002147] p-5 shadow-sm">
                <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Active Filters</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {appliedFilters.minCGPA != null && (
                    <span className="rounded-lg bg-slate-100 dark:bg-[#001f40] px-3 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300">
                      CGPA ≥ {appliedFilters.minCGPA}
                    </span>
                  )}
                  {(appliedFilters.skills || []).map((s) => (
                    <span key={s} className="rounded-lg bg-violet-50 dark:bg-violet-950/30 border border-violet-100 dark:border-violet-900/40 px-3 py-1.5 text-xs font-bold text-violet-700 dark:text-violet-300">
                      Must have: {s}
                    </span>
                  ))}
                  {appliedFilters.location && (
                    <span className="rounded-lg bg-slate-100 dark:bg-[#001f40] px-3 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300">
                      Location: {appliedFilters.location}
                    </span>
                  )}
                  {appliedFilters.minCGPA == null && !(appliedFilters.skills || []).length && !appliedFilters.location && (
                    <span className="text-xs text-slate-400">No extra filters — all college students ranked</span>
                  )}
                </div>
              </div>
            )}

            <div className="rounded-2xl border border-slate-100 dark:border-[var(--placement-primary-border)]/30 bg-white dark:bg-[#002147] p-5 shadow-sm">
              <h3 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-widest">Matching Logic</h3>
              <div className="mt-4 grid grid-cols-1 gap-3">
                {['Extract skills from JD text', 'Apply CGPA, skill, and location filters', 'Rank top candidates by skill percentage'].map((item) => (
                  <div key={item} className="flex items-center gap-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-[var(--placement-primary)]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Card className={`border-none shadow-xl overflow-hidden p-0 ${isDark ? 'bg-[#002147]' : 'bg-white'}`}>
        <div className="bg-white dark:bg-[#002147] border-b border-slate-100 dark:border-[var(--placement-primary-border)]/30 p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-[var(--placement-primary)] flex items-center justify-center shadow-lg shadow-[var(--placement-primary-border)]/15">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-black text-slate-950 dark:text-white">Top Matched Students</h3>
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500">Ranked by JD skill percentage</p>
            </div>
          </div>
          <span className="rounded-xl bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary)]/20 border border-[var(--placement-primary-border)] dark:border-[var(--placement-primary-border)]/35 px-4 py-2 text-xs font-black text-[var(--placement-primary)] dark:text-[var(--placement-primary)]">
            Showing {results.length} of 20
          </span>
        </div>

        {results.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 dark:bg-[#001f40] border-b border-slate-100 dark:border-[var(--placement-primary-border)]/30">
                <tr>
                  <th className="py-4 pl-6 text-xs font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest">Rank</th>
                  <th className="py-4 text-xs font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest">Student</th>
                  <th className="py-4 text-xs font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest">Skill Match</th>
                  <th className="py-4 text-xs font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest">CGPA</th>
                  <th className="py-4 text-xs font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest">Location</th>
                  <th className="py-4 text-xs font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest">Matched</th>
                  <th className="py-4 pr-6 text-xs font-black text-slate-500 dark:text-slate-300 uppercase tracking-widest">Gaps</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-[#1a3884]/30">
                {results.map((item, index) => (
                  <tr key={item.student._id} className="hover:bg-slate-50/70 dark:hover:bg-[#001f40]/70 transition-colors">
                    <td className="py-5 pl-6">
                      <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-[var(--placement-primary)] text-xs font-black text-white">
                        {index + 1}
                      </span>
                    </td>
                    <td className="py-5">
                      <div className="font-black text-slate-900 dark:text-white">{item.student.fullName}</div>
                      <div className="text-xs font-bold text-slate-400 mt-1">{item.student.studentId || item.student.rollNumber || item.student.email}</div>
                    </td>
                    <td className="py-5 min-w-[180px]">
                      <div className="flex items-center gap-3">
                        <div className="h-2.5 w-28 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${matchScoreBarColor(item.skillMatchPercentage)}`}
                            style={{ width: `${item.skillMatchPercentage}%` }}
                          />
                        </div>
                        <span className={`text-base font-black ${matchScoreTextColor(item.skillMatchPercentage)}`}>
                          {item.skillMatchPercentage}%
                        </span>
                      </div>
                      <p className="text-[10px] font-semibold text-slate-400 mt-1">
                        {item.totalMatchedSkills}/{item.totalRequiredSkills} skills
                      </p>
                    </td>
                    <td className="py-5 text-sm font-black text-slate-700 dark:text-slate-200">
                      {formatCGPA(item.placementProfile.currentCGPA)}
                    </td>
                    <td className="py-5 text-sm font-semibold text-slate-500 dark:text-slate-400 max-w-[140px] truncate" title={item.student.location}>
                      {item.student.location}
                    </td>
                    <td className="py-5">
                      <div className="flex flex-wrap gap-1.5 max-w-[200px]">
                        {item.matchedSkills?.length > 0 ? item.matchedSkills.slice(0, 4).map((skill) => (
                          <span key={skill} className="rounded-lg bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary-light)] border border-[var(--placement-primary-border)] dark:border-[var(--placement-primary-border)] px-2.5 py-1 text-[11px] font-bold text-[var(--placement-primary)] dark:text-[var(--placement-primary)]">
                            {skill}
                          </span>
                        )) : (
                          <span className="text-xs font-semibold text-slate-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="py-5 pr-6">
                      <div className="flex flex-wrap gap-1.5 max-w-[200px]">
                        {item.missingSkills?.length > 0 ? item.missingSkills.slice(0, 3).map((skill) => (
                          <span key={skill} className="rounded-lg bg-rose-50 dark:bg-rose-950/20 border border-rose-100 dark:border-rose-900/30 px-2.5 py-1 text-[11px] font-bold text-rose-600 dark:text-rose-300">
                            {skill}
                          </span>
                        )) : (
                          <span className="text-xs font-semibold text-[var(--placement-primary)]">Complete match</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-16 text-center">
            <FileText className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
            <h3 className="mt-4 text-lg font-black text-slate-900 dark:text-white">No analyzed candidates yet</h3>
            <p className="mt-2 text-sm font-medium text-slate-500 dark:text-slate-400">Paste a JD and run analysis to generate the top 20 student matches.</p>
          </div>
        )}
      </Card>
    </div>
  );
};

const PlacementOffice = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const hasPlacementFeature = ['admin', 'college_admin'].includes(user?.role) || user?.college?.subscriptionPlan?.addons?.collegePlacement;

  const [activeTab, setActiveTab] = useState('smaart_partners');
  const [placementSubTab, setPlacementSubTab] = useState('smaart');
  const [placementFilterBatch, setPlacementFilterBatch] = useState('');
  const [placementFilterDegree, setPlacementFilterDegree] = useState('');
  const [placementFilterDate, setPlacementFilterDate] = useState('');
  const [placementFilterCompany, setPlacementFilterCompany] = useState('');
  const [placementFilterStatus, setPlacementFilterStatus] = useState('All');
  const [confirmStatusModal, setConfirmStatusModal] = useState({ isOpen: false, appId: null, status: null, appData: null });
  const [aiDraftLoading, setAiDraftLoading] = useState(false);
  const [aiDraftMessage, setAiDraftMessage] = useState('');
  const [sendEmailWithUpdate, setSendEmailWithUpdate] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [bulkConfirmModal, setBulkConfirmModal] = useState({ isOpen: false, status: null });
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [jobFairs, setJobFairs] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [applications, setApplications] = useState([]);
  const { searchQuery, setSearchQuery, debouncedSearchQuery: debouncedSearch, searchInputRef } = useGlobalSearch('', 400);
  const [activePieIndex, setActivePieIndex] = useState(-1);

  // Modal states

  const [isJobModalOpen, setIsJobModalOpen] = useState(false);
  const [isJobFairModalOpen, setIsJobFairModalOpen] = useState(false);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [viewCompany, setViewCompany] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // New Placement Tab states
  const [selectedPlacementJob, setSelectedPlacementJob] = useState(null);
  const [placementCandidates, setPlacementCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [showApplicantModal, setShowApplicantModal] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [placementViewMode, setPlacementViewMode] = useState('kanban');
  const [selectedJobFair, setSelectedJobFair] = useState(null);
  // Active Job Postings card menu + the shared recruiter details modal
  const [openJobMenu, setOpenJobMenu] = useState(null);
  const [viewJobDetails, setViewJobDetails] = useState(null);

  const getNormalizedStatus = (status) => (status || '').toString().toLowerCase().trim();
  const getStatusBadgeClass = (status) => {
    const normalized = getNormalizedStatus(status);
    switch (normalized) {
      case 'shortlisted': return 'bg-amber-100 text-amber-800 border border-amber-300 dark:bg-amber-950/40 dark:border-amber-900/50 dark:text-amber-400';
      case 'interviewing': return 'bg-indigo-100 text-indigo-800 border border-indigo-300 dark:bg-indigo-950/40 dark:border-indigo-900/50 dark:text-indigo-400';
      case 'selected': return 'bg-emerald-100 text-emerald-800 border border-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-900/50 dark:text-emerald-400';
      case 'offer': return 'bg-blue-100 text-blue-800 border border-blue-300 dark:bg-blue-950/40 dark:border-blue-900/50 dark:text-blue-400';
      case 'accepted':
      case 'hired': return 'bg-green-100 text-green-800 border border-green-300 dark:bg-green-950/40 dark:border-green-900/50 dark:text-green-400';
      case 'declined': return 'bg-orange-100 text-orange-800 border border-orange-300 dark:bg-orange-950/40 dark:border-orange-900/50 dark:text-orange-400';
      case 'rejected': return 'bg-red-100 text-red-800 border border-red-300 dark:bg-red-950/40 dark:border-red-900/50 dark:text-red-400';
      default: return 'bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-800/60 dark:border-slate-700 dark:text-slate-300';
    }
  };
  const formatStatusText = (status) => {
    const normalized = getNormalizedStatus(status);
    if (normalized === 'pending') return 'Pending';
    if (normalized === 'shortlisted') return 'Shortlisted';
    if (normalized === 'interviewing') return 'Interviewing';
    if (normalized === 'selected') return 'Selected';
    if (normalized === 'offer') return 'Offer Sent (In Progress)';
    if (normalized === 'accepted') return 'Accepted';
    if (normalized === 'hired') return 'Hired';
    if (normalized === 'declined') return 'Declined';
    if (normalized === 'rejected') return 'Rejected';
    return status?.charAt(0).toUpperCase() + status?.slice(1);
  };

  const fetchAllData = useCallback(async () => {
    try {
      setLoading(true);
      const [statsRes, companyRes, jobRes, appRes, jobFairRes, recruiterRes] = await Promise.all([
        api.get('/placements/stats').catch(e => ({ data: { success: false } })),
        api.get('/placements/companies').catch(e => ({ data: { success: false } })),
        api.get('/placements/jobs').catch(e => ({ data: { success: false } })),
        api.get('/placements/applications').catch(e => ({ data: { success: false } })),
        api.get('/placements/job-fairs').catch(e => ({ data: { success: false } })),
        api.get('/users?role=recruiter').catch(e => ({ data: { success: false } }))
      ]);

      if (statsRes && statsRes.data?.success) setStats(statsRes.data.data);
      if (companyRes && companyRes.data?.success) setCompanies(companyRes.data.data);
      if (jobRes && jobRes.data?.success) setJobs(jobRes.data.data);
      if (appRes && appRes.data?.success) setApplications(appRes.data.data);
      if (jobFairRes && jobFairRes.data?.success) setJobFairs(jobFairRes.data.data);
      if (recruiterRes && recruiterRes.data?.success) setRecruiters(recruiterRes.data.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to sync placement records');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDeleteCompany = async (companyId) => {
    if (window.confirm("Are you sure you want to delete this hiring partner?")) {
      try {
        await api.delete(`/placements/companies/${companyId}`);
        toast.success("Hiring partner deleted successfully");
        fetchAllData();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete hiring partner");
      }
    }
  };


  const [searchParams] = useSearchParams();

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Close the job card menu on any outside click, matching the fair cards.
  useEffect(() => {
    if (!openJobMenu) return undefined;
    const handler = () => setOpenJobMenu(null);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openJobMenu]);

  // Refresh applications with fresh matchData whenever a placement job detail view is opened
  useEffect(() => {
    if (selectedPlacementJob) {
      api.get('/placements/applications').then(res => {
        if (res.data?.success) setApplications(res.data.data);
      }).catch(() => {});
    }
  }, [selectedPlacementJob]);

  useEffect(() => {
    const jobId = searchParams.get('jobId');
    const fairId = searchParams.get('fairId');
    if (jobId) {
      if (jobs.length > 0) {
        const matchedJob = jobs.find(j => j._id === jobId);
        if (matchedJob) {
          setSelectedJob(matchedJob);
          setActiveTab('jobs');
          setIsJobModalOpen(true);
          return;
        }
      }
    }
    if (fairId && jobFairs.length > 0) {
      const matchedFair = jobFairs.find(f => f._id === fairId);
      if (matchedFair) {
        setSelectedJobFair(matchedFair);
        setActiveTab('job_fairs');
        setIsJobFairModalOpen(true);
      }
    }
  }, [searchParams, jobs, jobFairs]);

  const tabs = [
    { id: 'smaart_partners', name: 'SMAART Partners', icon: Users, color: 'indigo' },
    { id: 'college_partners', name: 'College Partners', icon: Users, color: 'amber' },
    { id: 'job_fairs', name: 'Job Fairs', icon: Calendar, color: 'indigo' },
    { id: 'placements', name: 'Placements', icon: CheckCircle2, color: 'emerald' }
  ];

  const allCompanies = [
    ...companies,
    ...recruiters.filter(r => ['Approved', 'active'].includes(r.status)).map(r => ({
      _id: r._id,
      name: r.fullName || r.qualification || '',
      industry: r.companyType || '',
      website: r.website || '',
      logo: r.profileImage || '',
      description: r.aboutCompany || '',
      contactEmail: r.email || '',
      contactPhone: r.mobile || '',
      college: r.college,
      branches: r.branches || []
    }))
  ];

  const filteredCompanies = allCompanies.filter(c =>
    (c.name && c.name.toLowerCase().includes(debouncedSearch.toLowerCase())) ||
    (c.industry && c.industry.toLowerCase().includes(debouncedSearch.toLowerCase()))
  );

  const filteredJobs = jobs.filter(j =>
    j.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    j.company?.name.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const filteredJobFairs = jobFairs.filter(f =>
    f.title.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    f.location.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  if (loading && !stats) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center space-y-4">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          className="w-12 h-12 border-4 border-[var(--placement-primary-border)] border-t-[#1a3884] rounded-full"
        />
        <p className="text-gray-400 font-medium animate-pulse">Synchronizing Records...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col gap-6 max-w-7xl mx-auto">
      {/* Dynamic Background Element */}
      <div className="fixed top-0 right-0 -z-10 w-1/3 h-1/3 bg-[var(--placement-primary-light)]/30 dark:bg-[var(--placement-primary)]/10 blur-[120px] rounded-full pointer-events-none" />

      {/* Header Section */}
      <div className={`border border-gray-100 dark:border-[var(--placement-primary-border)]/20 rounded-[1.25rem] px-6 py-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4 ${isDark ? 'bg-[#002147]' : 'bg-white'}`}>
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard')} className="p-2 rounded-xl border border-gray-200 dark:border-[var(--placement-primary-border)]/30 hover:bg-gray-50 dark:hover:bg-[var(--placement-primary-hover)]/20 text-gray-500 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="space-y-0.5">
            <h1 className="text-[22px] font-black text-[var(--placement-primary)] dark:text-[var(--placement-primary)] tracking-tight">
              Placement Overview
            </h1>
            <div className="flex items-center gap-1.5 text-[13px] font-medium text-gray-500 dark:text-gray-400">
              <Activity className="w-4 h-4 text-[var(--placement-primary)] opacity-60" />
              Manage all placement drives and their details
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={fetchAllData}
            disabled={loading}
            className={`border border-gray-200 dark:border-[var(--placement-primary-border)]/30 hover:bg-gray-50 dark:hover:bg-[var(--placement-primary-hover)]/20 text-gray-700 dark:text-gray-200 rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 transition-all shadow-sm ${isDark ? 'bg-[#001f40]' : 'bg-white'}`}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {hasPlacementFeature && (
            <>
              <button
                onClick={() => setIsJobFairModalOpen(true)}
                className="bg-white dark:bg-[#001430] border border-[#1a3884]/20 hover:border-[#1a3884] dark:border-[#1a3884]/40 hover:bg-[#f8faff] dark:hover:bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 transition-all shadow-sm group"
              >
                <IconCalendarEvent stroke={2.5} className="w-4 h-4 text-[#1a3884]/70 group-hover:text-[#1a3884] dark:text-[#4f7dff]/70 dark:group-hover:text-[#4f7dff] transition-colors" />
                Add Job Fair
              </button>
              <button
                onClick={() => navigate('/college/add-recruiter')}
                className="bg-white dark:bg-[#001430] border border-[#1a3884]/20 hover:border-[#1a3884] dark:border-[#1a3884]/40 hover:bg-[#f8faff] dark:hover:bg-[#1a3884]/20 text-[#1a3884] dark:text-[#4f7dff] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 transition-all shadow-sm group"
              >
                <IconBuilding stroke={2.5} className="w-4 h-4 text-[#1a3884]/70 group-hover:text-[#1a3884] dark:text-[#4f7dff]/70 dark:group-hover:text-[#4f7dff] transition-colors" />
                Add Partner
              </button>
              <button
                onClick={() => setIsJobModalOpen(true)}
                className="bg-[#1a3884] hover:bg-[#0f225c] text-white border border-[#1a3884] hover:border-[#0f225c] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-2 transition-all shadow-[0_4px_12px_rgba(26,56,132,0.25)] group"
              >
                <IconBriefcase stroke={2.5} className="w-4 h-4 text-white/90 group-hover:text-white transition-colors" />
                Post Opportunity
              </button>
            </>
          )}
        </div>
      </div>


      {/* Modern Tabs Bar */}
      <div className="relative flex items-center gap-1 p-1.5 bg-gray-50 dark:bg-[#001f40]/80 rounded-[1.25rem] w-full border border-gray-100 dark:border-[var(--placement-primary-border)]/30 overflow-x-auto shadow-sm">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`relative flex items-center justify-center space-x-2.5 px-6 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 z-10 whitespace-nowrap flex-1 ${activeTab === tab.id ? 'text-white' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
          >
            <tab.icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-white' : 'text-gray-400 dark:text-gray-500'}`} />
            <span>{tab.name}</span>
            {activeTab === tab.id && (
              <motion.div
                layoutId="activeTab"
                className="absolute inset-0 bg-[var(--placement-primary)] rounded-xl shadow-sm -z-10"
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Main Content Area */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -15 }}
          transition={{ duration: 0.3 }}
        >

          {activeTab === 'jd_analyzer' && (
            <JDAnalyzer isDark={isDark} />
          )}



          {(activeTab === 'companies' || activeTab === 'smaart_partners' || activeTab === 'college_partners') && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div className="relative w-full max-w-md group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400 group-focus-within:text-[var(--placement-primary)] transition-colors" />
                  <input
                    ref={activeTab === 'companies' ? searchInputRef : null}
                    type="text"
                    placeholder="Search partners by name or industry..."
                    className="w-full pl-12 pr-4 py-3 bg-white dark:bg-[#001f40] border border-gray-200 dark:border-[var(--placement-primary-border)]/40 text-gray-900 dark:text-white rounded-xl focus:ring-4 focus:ring-[var(--placement-primary-border)]/20 dark:focus:ring-[var(--placement-primary-border)]/20 focus:border-[var(--placement-primary-border)] dark:focus:border-[var(--placement-primary)] transition-all font-medium placeholder:text-gray-400 dark:placeholder:text-gray-500"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {(() => {
                  const displayCompanies = filteredCompanies.filter(c => activeTab === 'smaart_partners' ? !c.college : activeTab === 'college_partners' ? !!c.college : true);
                  return displayCompanies.length > 0 ? (
                  displayCompanies.map((company, idx) => (
                    <motion.div
                      key={company._id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <CompanyCard
                        company={company}
                        jobs={jobs}
                        applications={applications}
                        onEdit={(c) => { if (c.college) navigate('/college/edit-recruiter/' + c._id); else toast.error('Cannot edit global SMAART partners'); }}
                        onDelete={(id) => { const c = allCompanies.find(x => x._id === id); if (c && c.college) handleDeleteCompany(id); else toast.error('Cannot delete global SMAART partners'); }}
                        onView={(c) => setViewCompany(c)}
                        isDark={isDark}
                      />
                    </motion.div>
                  ))
                ) : (
                  <EmptyState
                    icon={Building2}
                    title="No Partners Found"
                    desc="We couldn't find any hiring partners matching your search. Try adding a new company!"
                    action={() => { navigate('/college/add-recruiter'); }}
                    actionLabel="Add Partner"
                  />
                );
                })()}
              </div>
            </div>
          )}

          {activeTab === 'jobs' && (
            <div className="space-y-6 max-w-5xl mx-auto">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400 group-focus-within:text-[var(--placement-primary)] transition-colors" />
                  <input
                    ref={activeTab === 'jobs' ? searchInputRef : null}
                    type="text"
                    placeholder="Search partners by name or industry..."
                    className={`w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-[var(--placement-primary-border)]/40 text-gray-900 dark:text-white rounded-xl focus:ring-4 focus:ring-[var(--placement-primary-border)]/20 dark:focus:ring-[var(--placement-primary-border)]/20 focus:border-[var(--placement-primary-border)] dark:focus:border-[var(--placement-primary)] transition-all font-medium placeholder:text-gray-400 dark:placeholder:text-gray-500 ${isDark ? 'bg-[#001f40]' : 'bg-white'}`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                {filteredJobs.length > 0 ? (
                  filteredJobs.map((job) => (
                    <JobCard
                      key={job._id}
                      job={job}
                      onEdit={(j) => { setSelectedJob(j); setIsJobModalOpen(true); }}
                      onDelete={async (id) => {
                        if (!window.confirm('Delete this job drive? This cannot be undone.')) return;
                        try {
                          await api.delete(`/placements/jobs/${id}`);
                          toast.success('Job drive deleted successfully');
                          fetchAllData();
                        } catch (err) {
                          toast.error(err.response?.data?.message || 'Failed to delete job drive');
                        }
                      }}
                      onToggleStatus={async (jobToToggle) => {
                        const newStatus = ['open', 'active'].includes(jobToToggle.status) ? 'inactive' : 'active';
                        try {
                          await api.put(`/placements/jobs/${jobToToggle._id}`, { status: newStatus });
                          toast.success(`Job marked as ${newStatus.toUpperCase()}`);
                          fetchAllData();
                        } catch (err) {
                          toast.error(err.response?.data?.message || 'Failed to update status');
                        }
                      }}
                      isDark={isDark}
                    />
                  ))
                ) : (
                  <EmptyState icon={Target} title="No Active Drives" desc="Start a new recruitment drive to find jobs for your students." action={() => setIsJobModalOpen(true)} actionLabel="Post Drive" />
                )}
              </div>
            </div>
          )}

          {activeTab === 'job_fairs' && (
            <div className="space-y-6 max-w-5xl mx-auto">
              <div className="flex items-center space-x-4">
                <div className="flex-1 relative group">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-400 group-focus-within:text-[#4f7dff] transition-colors" />
                  <input
                    ref={activeTab === 'job_fairs' ? searchInputRef : null}
                    type="text"
                    placeholder="Search fairs by title or location..."
                    className={`w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-[#1a3884]/40 text-gray-900 dark:text-white rounded-xl focus:ring-4 focus:ring-[#1a3884]/20 dark:focus:ring-[#1a3884]/20 focus:border-[#1a3884] dark:focus:border-[#4f7dff] transition-all font-medium placeholder:text-gray-400 dark:placeholder:text-gray-500 ${isDark ? 'bg-[#001f40]' : 'bg-white'}`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-5 md:grid-cols-2">
                {filteredJobFairs.length > 0 ? (
                  filteredJobFairs.map((fair) => (
                    <JobFairCard
                      key={fair._id}
                      fair={fair}
                      onEdit={(f) => { setSelectedJobFair(f); setIsJobFairModalOpen(true); }}
                      onDelete={async (id) => {
                        if (!window.confirm('Delete this Job Fair? This cannot be undone.')) return;
                        try {
                          await api.delete(`/placements/job-fairs/${id}`);
                          toast.success('Job Fair deleted successfully');
                          fetchAllData();
                        } catch (err) {
                          toast.error(err.response?.data?.message || 'Failed to delete Job Fair');
                        }
                      }}
                      isDark={isDark}
                    />
                  ))
                ) : (
                  <EmptyState icon={Calendar} title="No Active Job Fairs" desc="Start a new Job Fair event to coordinate multiple recruiters and companies for your students." action={() => { setSelectedJobFair(null); setIsJobFairModalOpen(true); }} actionLabel="Create Fair" />
                )}
              </div>
            </div>
          )}

          {activeTab === 'placements' && (() => {
            if (selectedPlacementJob) {
              // Show Pipeline Board for selected job
              const jobCandidates = applications.filter(app => app.jobPosting?._id === selectedPlacementJob._id || app.job === selectedPlacementJob._id);
              
              const mappedCandidates = jobCandidates.map(c => ({
                id: c._id,
                _id: c._id,
                name: c.student?.fullName || c.studentName || 'Unknown Student',
                fullName: c.student?.fullName || c.studentName || 'Unknown Student',
                role: selectedPlacementJob.title,
                company: selectedPlacementJob.company?.name || c.companyName || 'Corporate Partner',
                // The College ref is populated as 'collegeName name', so collegeName is
                // the real field — reading .name first always fell through to the
                // placeholder and showed "SMAART INSTITUTE" for every candidate.
                collegeName: c.student?.college?.collegeName || c.student?.college?.name || c.student?.collegeName || 'SMAART INSTITUTE',
                status: c.status || 'Applied',
                matchScore: c.matchData?.skillMatch?.score ?? c.matchScore ?? 0,
                matchData: c.matchData || {},
                appliedDate: c.createdAt || c.appliedAt,
                appliedAt: c.createdAt || c.appliedAt,
                skills: c.student?.verifiedSkills || c.student?.skills || c.skills || [],
                academicMatch: c.matchData?.academicMatch || {},
                avatar: c.student?.profilePic || c.student?.profileImage,
                profileImage: c.student?.profilePic || c.student?.profileImage,
                resumeUrl: c.resumeUrl || c.student?.resumeUrl,
                interviewDetails: c.interviewDetails || {},
                _raw: c
              }));


              return (
                <div className="space-y-6">
                  <div className="flex items-center justify-between bg-white dark:bg-[#002147] p-4 rounded-2xl border border-gray-200 dark:border-[#1a3884]/25 shadow-sm">
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={() => setSelectedPlacementJob(null)}
                        className="p-2 rounded-lg bg-gray-50 dark:bg-[#00152E] hover:bg-gray-100 dark:hover:bg-[#001f40] text-gray-600 dark:text-gray-300 transition-colors"
                      >
                        <ArrowLeft className="w-5 h-5" />
                      </button>
                      <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                          <Briefcase className="w-5 h-5 text-[#1a3884] dark:text-[#4f7dff]" />
                          {selectedPlacementJob.title}
                        </h2>
                        <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mt-0.5">
                          {selectedPlacementJob.company?.name || 'Corporate Partner'} • {jobCandidates.length} Candidates
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center bg-gray-100 dark:bg-[#001733] p-1 rounded-xl">
                      <button onClick={() => setPlacementViewMode('list')} className={`p-2 px-4 rounded-lg flex items-center gap-2 text-sm font-semibold transition-all ${placementViewMode === 'list' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
                        <ListIcon size={16} /> List
                      </button>
                      <button onClick={() => setPlacementViewMode('kanban')} className={`p-2 px-4 rounded-lg flex items-center gap-2 text-sm font-semibold transition-all ${placementViewMode === 'kanban' ? 'bg-white dark:bg-[#1a3884] text-[#1a3884] dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}>
                        <LayoutDashboard size={16} /> Pipeline Board
                      </button>
                    </div>
                  </div>

                  {placementViewMode === 'kanban' ? (
                    <PipelineBoard
                      applications={mappedCandidates}
                      // Prop is onStatusUpdate, not onStatusChange — the old name was
                      // silently ignored, so dragging a card never saved the status.
                      onStatusUpdate={async (appId, newStatus) => {
                        try {
                          await api.put(`/placements/applications/${appId}/status`, { status: newStatus });
                          toast.success(`Status updated to ${newStatus}`);
                        } catch (err) {
                          toast.error('Failed to update status');
                        }
                      }}
                      onRefresh={fetchAllData}
                      // Open the parent's modal, which receives the full applicant
                      // (job details, matchData, college) rather than the flat card shape.
                      onViewCandidate={(c) => {
                        setSelectedApplicant(c._raw || c);
                        setShowApplicantModal(true);
                      }}
                    />
                  ) : (
                    <div className="space-y-4">
                      {/* Applicant list — table on desktop, stacked cards on mobile */}
                      <div className="bg-white dark:bg-[#002147] border border-slate-200 dark:border-[#1a3884]/25 rounded-2xl shadow-sm overflow-hidden">
                        {/* Desktop table header */}
                        <div className="hidden lg:grid grid-cols-12 gap-4 px-5 py-3 bg-slate-50 dark:bg-[#001f40] border-b border-slate-200 dark:border-[#1a3884]/25 text-[10px] font-bold text-slate-500 dark:text-slate-300 uppercase tracking-widest">
                          <div className="col-span-3 pl-1">Candidate</div>
                          <div className="col-span-3">Qualification &amp; College</div>
                          <div className="col-span-2">Match Score</div>
                          <div className="col-span-2">Date Applied</div>
                          <div className="col-span-1">Status</div>
                          <div className="col-span-1 text-right"></div>
                        </div>

                        <div className="divide-y divide-slate-100 dark:divide-[#1a3884]/25">
                          {mappedCandidates.length === 0 ? (
                            <div className="text-center py-12 text-gray-500 font-medium">No candidates found for this role.</div>
                          ) : mappedCandidates.map((candidate) => (
                            <div
                              key={candidate.id}
                              onClick={() => {
                                setSelectedApplicant(candidate._raw);
                                setShowApplicantModal(true);
                              }}
                              className="lg:grid lg:grid-cols-12 lg:gap-4 lg:items-center px-5 py-3.5 flex flex-col gap-4 hover:bg-slate-50/70 dark:hover:bg-[#001c3d]/60 cursor-pointer transition-colors group"
                            >
                              {/* Candidate: avatar + name + email */}
                              <div className="lg:col-span-3 flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-lg bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 border border-[#1a3884]/10 dark:border-[#4f7dff]/15 flex items-center justify-center flex-shrink-0 text-[#1a3884] dark:text-[#4f7dff] font-bold text-sm overflow-hidden">
                                  {candidate.avatar ? (
                                    <img src={candidate.avatar} alt={candidate.name} className="w-full h-full object-cover" />
                                  ) : (
                                    candidate.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <h4 className="font-bold text-slate-900 dark:text-white text-sm truncate">{candidate.name}</h4>
                                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                    <Mail className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                    <span className="truncate">{candidate._raw?.student?.email || 'N/A'}</span>
                                  </div>
                                  <div className="flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                    <Phone className="w-3 h-3 text-slate-400 flex-shrink-0" />
                                    <span>{candidate._raw?.student?.mobile || 'N/A'}</span>
                                  </div>
                                </div>
                              </div>

                              {/* Qualification / College */}
                              <div className="lg:col-span-3 min-w-0">
                                <div className="flex items-center gap-1.5 text-[13px] text-slate-700 dark:text-slate-200 font-bold truncate">
                                  <GradCap className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                  <span className="truncate">{candidate.role}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
                                  <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                                  <span className="truncate">{candidate.collegeName}</span>
                                </div>
                                {candidate.skills && candidate.skills.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1.5">
                                    {candidate.skills.slice(0, 3).map((skill, index) => (
                                      <span
                                        key={index}
                                        className="text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-slate-100 dark:bg-[#001733] text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 shadow-sm"
                                      >
                                        {typeof skill === 'string' ? skill : (skill.skill_name || skill.name || '')}
                                      </span>
                                    ))}
                                    {candidate.skills.length > 3 && (
                                      <span className="text-[10px] font-bold text-slate-400 px-1 py-0.5">
                                        +{candidate.skills.length - 3}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Match Score */}
                              <div className="lg:col-span-2">
                                {(() => {
                                  const skillMatch = candidate.matchData?.skillMatch || candidate._raw?.matchData?.skillMatch || {};
                                  const isUnscored = skillMatch.notScored === true || !(skillMatch.breakdown?.skillContributions || []).length;
                                  
                                  if (!skillMatch.band && candidate.matchScore) {
                                    return (
                                      <div className="flex flex-col gap-1.5">
                                        <div className="w-24 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                          <div className="h-full bg-[#1a3884] rounded-full" style={{ width: candidate.matchScore + '%' }} />
                                        </div>
                                        <span className="text-[10px] w-fit px-1.5 py-0.5 rounded-md border font-bold bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700">
                                          {candidate.matchScore}% Match
                                        </span>
                                      </div>
                                    );
                                  }
                                  
                                  if (!skillMatch.band) return <span className="text-xs text-slate-400">N/A</span>;
                                  
                                  const bandColor = (() => {
                                      switch (skillMatch.band) {
                                          case 'Strong': return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800/50';
                                          case 'Good': return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800/50';
                                          case 'Partial': return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800/50';
                                          case 'Not a Match': return 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-400 dark:border-rose-800/50';
                                          default: return 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
                                      }
                                  })();
                                  
                                  return (
                                    <div className="flex flex-col gap-1.5 justify-center">
                                      {!isUnscored && skillMatch.score > 0 && (
                                        <div className="w-24 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mt-1">
                                          <div className="h-full rounded-full" style={{ width: (skillMatch.score || 0) + '%', backgroundColor: skillMatch.band === 'Strong' ? '#10b981' : skillMatch.band === 'Good' ? '#3b82f6' : skillMatch.band === 'Partial' ? '#f59e0b' : '#f43f5e' }} />
                                        </div>
                                      )}
                                      <span className={`text-[10px] w-fit px-1.5 py-0.5 rounded-md border font-bold ${bandColor}`}>
                                        {isUnscored ? 'Skills not scored' : `Skills ${skillMatch.score}% · ${skillMatch.band}`}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Applied date */}
                              <div className="lg:col-span-2">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300">
                                  <Calendar className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 lg:hidden xl:inline" />
                                  <span>{new Date(candidate.appliedDate || candidate.appliedAt).toLocaleDateString()}</span>
                                </div>
                              </div>

                              {/* Status */}
                              <div className="lg:col-span-1">
                                <span className={`inline-flex items-center justify-center px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-widest shadow-sm ${getStatusBadgeClass(candidate.status)}`}>
                                  {formatStatusText(candidate.status)}
                                </span>
                              </div>

                              {/* Chevron Right */}
                              <div className="hidden lg:flex col-span-1 justify-end">
                                <div className="w-8 h-8 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center group-hover:bg-[#1a3884] group-hover:border-[#1a3884] dark:group-hover:bg-[#4f7dff] dark:group-hover:border-[#4f7dff] transition-colors">
                                  <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  <ApplicantPackModal 
                    isOpen={showApplicantModal}
                    onClose={() => { setShowApplicantModal(false); setSelectedApplicant(null); }}
                    applicant={
                      selectedApplicant ? {
                        ...selectedApplicant,
                        applicationId: selectedApplicant._id,
                        fullName: selectedApplicant.student?.fullName || selectedApplicant.studentName,
                        email: selectedApplicant.student?.email || selectedApplicant.studentEmail,
                        mobile: selectedApplicant.student?.mobile || selectedApplicant.studentMobile,
                        jobTitle: selectedPlacementJob.title,
                        jobDetails: selectedPlacementJob,
                        job: selectedPlacementJob,
                        collegeName: selectedApplicant.student?.college?.collegeName || selectedApplicant.student?.college?.name || user?.college?.name || 'Your College',
                        // Same resolution the recruiter side uses: fall back to the
                        // academic record when no degree is linked, and collapse
                        // array specialisations to text.
                        qualification: studentDegreeText(selectedApplicant.student),
                        degreeFullName: asText(selectedApplicant.student?.academic?.degreeGroup)
                          || asText(selectedApplicant.student?.degree?.fullName)
                          || studentDegreeText(selectedApplicant.student),
                        branch: studentBranchText(selectedApplicant.student),
                        specialization: studentBranchText(selectedApplicant.student),
                        skills: firstNonEmpty(
                          selectedApplicant.student?.verifiedSkills,
                          selectedApplicant.student?.skills,
                          selectedApplicant.placementProfile?.skills,
                          selectedApplicant.skills
                        ),
                        verifiedSkills: firstNonEmpty(
                          selectedApplicant.student?.verifiedSkills,
                          selectedApplicant.student?.skills,
                          selectedApplicant.placementProfile?.skills,
                          selectedApplicant.skills
                        ),
                        profileImage: selectedApplicant.student?.profileImage || selectedApplicant.student?.profilePic || selectedApplicant.student?.profilePhoto,
                        cgpa: (() => {
                          const s = selectedApplicant.student || {};
                          const raw = s.cgpa || s.academic?.overallCgpa || s.academic?.cgpa || (s.academicRecords && s.academicRecords.length > 0 ? s.academicRecords[s.academicRecords.length - 1].cgpa : 0) || s.registration?.cgpa || selectedApplicant.cgpa || 0;
                          return raw ? Number(parseFloat(String(raw).replace(/[^0-9.]/g, '') || 0).toFixed(2)) : 0;
                        })(),
                        activeBacklog: (() => {
                          const s = selectedApplicant.student || {};
                          return s.activeBacklogs !== undefined ? s.activeBacklogs : (s.academic?.activeBacklogs !== undefined ? s.academic.activeBacklogs : (Array.isArray(s.activeArrears) ? s.activeArrears.length : (selectedApplicant.activeBacklog || 0)));
                        })(),
                        academicRecords: selectedApplicant.student?.academicRecords || [],
                        tenthDetails: selectedApplicant.student?.tenthDetails || selectedApplicant.student?.registration?.tenthDetails || null,
                        twelfthDetails: selectedApplicant.student?.twelfthDetails || selectedApplicant.student?.registration?.twelfthDetails || null,
                        resumeUrl: selectedApplicant.resumeUrl,
                        coverLetter: selectedApplicant.coverLetter,
                        offeredPackage: selectedApplicant.offeredPackage,
                        offerDate: selectedApplicant.offerDate,
                        recruiterSignature: selectedApplicant.recruiterSignature,
                        eSignature: selectedApplicant.eSignature,
                        signatureDate: selectedApplicant.signatureDate
                      } : null
                    }
                    onStatusUpdate={async (newStatus, payload = {}) => {
                      if (!selectedApplicant) return;
                      try {
                        await api.put(`/placements/applications/${selectedApplicant._id}/status`, { status: newStatus, ...payload });
                        toast.success(`Updated to ${newStatus}`);
                        setShowApplicantModal(false);
                        fetchAllData();
                      } catch (err) {
                        toast.error('Failed to update status');
                      }
                    }}
                  />
                </div>
              );
            }

            // Otherwise, show the Jobs List (Job Cards)
            const activeJobs = jobs.filter(j => j.status !== 'closed' && j.status !== 'cancelled');
            
            return (
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <Briefcase className="w-6 h-6 text-[#1a3884] dark:text-[#4f7dff]" />
                  <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight">Active Job Postings</h2>
                </div>
                
                {activeJobs.length === 0 ? (
                  <EmptyState icon={Target} title="No Active Jobs" desc="You don't have any active job postings. Start a new drive from the top menu." action={() => setIsJobModalOpen(true)} actionLabel="Post Opportunity" />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {activeJobs.map(job => {
                      const appCount = applications.filter(a => a.jobPosting?._id === job._id || a.job === job._id).length;
                      return (
                        <div key={job._id} className="bg-white dark:bg-[#002147] rounded-2xl p-5 border border-gray-200 dark:border-[#1a3884]/25 shadow-sm hover:shadow-md hover:border-[#1a3884]/40 transition-all flex flex-col h-full group">
                          <div className="flex items-start gap-4 mb-4">
                            <div className="w-12 h-12 rounded-xl bg-white dark:bg-[#00152E] border border-gray-100 dark:border-gray-800 shadow-sm flex items-center justify-center font-bold text-[#1a3884] dark:text-[#4f7dff] uppercase text-sm overflow-hidden shrink-0">
                              {job.company?.logo ? (
                                <img src={getFileUrl(job.company.logo)} alt={job.company.name} className="w-full h-full object-contain p-1.5" />
                              ) : (
                                <span>{(job.company?.name || 'CO').slice(0, 2)}</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-extrabold text-[17px] text-[#0f225c] dark:text-white line-clamp-1 group-hover:text-[#1a3884] transition-colors">{job.title}</h3>
                              <p className="text-[12px] font-bold text-[#1a3884]/70 uppercase tracking-widest truncate mt-0.5">{job.company?.name || 'Corporate Partner'}</p>
                            </div>

                            {/* Same View / Edit / Delete menu as the recruiter job cards */}
                            <div className="relative shrink-0" onMouseDown={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => setOpenJobMenu(openJobMenu === job._id ? null : job._id)}
                                className="p-1.5 rounded-lg text-slate-400 hover:text-[#1a3884] hover:bg-[#1a3884]/5 dark:hover:bg-[#001733] dark:hover:text-slate-200 transition-colors"
                              >
                                <MoreVertical size={16} />
                              </button>
                              {openJobMenu === job._id && (
                                <div className={`absolute right-0 mt-1.5 w-36 rounded-xl shadow-xl border p-1 z-30 animate-fade-in ${
                                  isDark ? 'bg-[#00152e] border-slate-700/50' : 'bg-white border-slate-100'
                                }`}>
                                  <button
                                    onClick={() => { setOpenJobMenu(null); setViewJobDetails(job); }}
                                    className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 transition-colors border-b border-gray-100 dark:border-[#1a3884]/25"
                                  >
                                    <Eye size={12} className="text-[#1a3884] dark:text-[#4f7dff]" />
                                    View Details
                                  </button>
                                  <button
                                    onClick={() => { setOpenJobMenu(null); setSelectedJob(job); setIsJobModalOpen(true); }}
                                    className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20 transition-colors"
                                  >
                                    <Edit size={12} className="text-[#1a3884] dark:text-[#4f7dff]" />
                                    Edit
                                  </button>
                                  <button
                                    onClick={async () => {
                                      setOpenJobMenu(null);
                                      if (!window.confirm('Delete this job posting? This cannot be undone.')) return;
                                      try {
                                        await api.delete(`/placements/jobs/${job._id}`);
                                        toast.success('Job posting deleted successfully');
                                        fetchAllData();
                                      } catch (err) {
                                        toast.error(err.response?.data?.message || 'Failed to delete job posting');
                                      }
                                    }}
                                    className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20 transition-colors border-t border-gray-100 dark:border-[#1a3884]/25"
                                  >
                                    <Trash2 size={12} className="text-red-500" />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex flex-wrap gap-2 mb-4">
                            {job.jobType && <span className="px-2.5 py-1 text-[10px] font-bold bg-[#1a3884]/5 text-[#1a3884] rounded-md border border-[#1a3884]/15">{job.jobType}</span>}
                            {job.workMode && <span className="px-2.5 py-1 text-[10px] font-bold bg-[#1a3884]/5 text-[#1a3884] rounded-md border border-[#1a3884]/15">{job.workMode}</span>}
                          </div>

                          <div className="grid grid-cols-2 gap-2 mb-5">
                            <div className="bg-gray-50 dark:bg-[#001733] p-2.5 rounded-xl border border-gray-100 dark:border-slate-800/60">
                              <div className="flex items-center gap-1.5 text-[11px] font-bold text-gray-500 mb-0.5"><Users className="w-3.5 h-3.5" /> Applicants</div>
                              <div className="text-lg font-black text-[#1a3884] dark:text-[#4f7dff]">{appCount}</div>
                            </div>
                            <div className="bg-emerald-50 dark:bg-emerald-950/20 p-2.5 rounded-xl border border-emerald-100 dark:border-emerald-900/30">
                              <div className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 mb-0.5"><IndianRupee className="w-3.5 h-3.5" /> Package</div>
                              <div className="text-sm font-black text-emerald-700 dark:text-emerald-400 truncate mt-1">{job.salaryPackage || 'N/A'}</div>
                            </div>
                          </div>

                          <button onClick={() => setSelectedPlacementJob(job)} className="mt-auto w-full py-2.5 bg-gray-50 dark:bg-[#00152E] hover:bg-[#1a3884] hover:text-white dark:hover:bg-[#4f7dff] dark:hover:text-white text-gray-700 dark:text-gray-300 rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-2 border border-gray-200 dark:border-[#1a3884]/30 hover:border-transparent group-hover:border-[#1a3884]/20">
                            <Users className="w-4 h-4" /> View Candidates
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

                  {false && (
            <Card className={`border-none shadow-2xl overflow-hidden p-0 ${isDark ? 'bg-[#002147]' : 'bg-white'}`}>
              <div className="bg-gradient-to-r from-gray-50 to-blue-50/20 dark:from-[#002147] dark:to-[#1a3884] border-b border-gray-150 dark:border-[var(--placement-primary-border)]/20 p-6 flex items-center justify-between">
                <div className="flex items-center space-x-3 text-gray-900 dark:text-white">
                  <GradCap className="w-6 h-6 border-2 border-[var(--placement-primary-border)] dark:border-white/30 text-[var(--placement-primary)] dark:text-white rounded-lg p-0.5" />
                  <div>
                    <h3 className="text-lg font-black tracking-tight">Recent Success Stories</h3>
                    <p className="text-[var(--placement-primary)] dark:text-[var(--placement-primary)] text-[10px] font-bold uppercase tracking-widest mt-0.5">Placement Register</p>
                  </div>
                </div>
                <div className="flex items-center font-bold text-gray-700 dark:text-white text-xs bg-[var(--placement-primary-light)] dark:bg-white/10 px-3.5 py-2 rounded-xl border border-[var(--placement-primary-border)]/50 dark:border-white/10">
                  Total: {applications.length} Students
                </div>
              </div>

              {/* Desktop View: Styled Table for wide viewports */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-gray-50/80 dark:bg-[#001f40] border-b border-gray-100 dark:border-[var(--placement-primary-border)]/30">
                    <tr>
                      <th className="py-5 pl-8 text-xs font-black text-gray-600 dark:text-slate-300 uppercase tracking-widest">Student Identification</th>
                      <th className="py-5 text-xs font-black text-gray-600 dark:text-slate-300 uppercase tracking-widest">Target Role / Firm</th>
                      <th className="py-5 text-xs font-black text-gray-600 dark:text-slate-300 uppercase tracking-widest">Career Status</th>
                      <th className="py-5 text-xs font-black text-gray-600 dark:text-slate-300 uppercase tracking-widest text-right pr-8">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-[#1a3884]/30 bg-transparent">
                    {applications.length > 0 ? (
                      applications.map((app) => (
                        <tr key={app._id} className="group hover:bg-gray-50/50 dark:hover:bg-[#001f40] transition-colors">
                          <td className="py-6 pl-8">
                            <div className="flex items-center space-x-4">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#00152E] to-[#1a3884] text-white flex items-center justify-center font-black text-sm shadow-lg shadow-[var(--placement-primary-border)]/20">
                                {app.student?.fullName ? app.student.fullName.charAt(0) : '?'}
                              </div>
                              <div className="flex flex-col">
                                <div className="font-bold text-gray-950 dark:text-white text-sm">{app.student?.fullName || 'N/A Student'}</div>
                                <div className="flex items-center text-[10px] text-gray-500 font-bold uppercase tracking-widest mt-1">
                                  <span className="text-[var(--placement-primary)] bg-[var(--placement-primary)]/10 dark:bg-[var(--placement-primary)]/30 px-1.5 py-0.5 rounded leading-none mr-2">
                                    {app.student?.studentId || app.student?.rollNumber || 'No ID'}
                                  </span>
                                  {app.student?.mobile && (
                                    <>
                                      <div className="w-1.5 h-1.5 bg-gray-300 dark:bg-slate-700 rounded-full mr-2" />
                                      <span>{app.student.mobile}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-6">
                            <div className="space-y-1">
                              <p className="font-bold text-gray-800 dark:text-gray-200 text-sm tracking-tight">{app.jobPosting?.title}</p>
                              <div className="flex items-center text-[10px] text-[var(--placement-primary)] dark:text-[#5a6ef6] font-black uppercase tracking-wider">
                                <Building2 className="w-3 h-3 mr-1" />
                                {app.jobPosting?.company?.name}
                              </div>
                            </div>
                          </td>
                          <td className="py-6">
                            <div className={`inline-flex items-center px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest shadow-sm border ${app.status === 'placed'
                              ? 'bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary-light)] border-[var(--placement-primary-border)] dark:border-[var(--placement-primary-border)] text-[var(--placement-primary)] dark:text-[var(--placement-primary)]'
                              : app.status === 'offered'
                                ? 'bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary-light)] border-[var(--placement-primary-border)] dark:border-[var(--placement-primary-border)] text-[var(--placement-primary)] dark:text-[var(--placement-primary)]'
                                : (app.status === 'interviewing' || app.status === 'interview')
                                  ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-100 dark:border-amber-900/20 text-amber-700 dark:text-amber-400'
                                  : (app.status === 'applied' || app.status === 'shortlisted')
                                    ? 'bg-violet-50 dark:bg-violet-950/30 border-violet-100 dark:border-violet-900/20 text-violet-700 dark:text-violet-400'
                                    : app.status === 'rejected'
                                      ? 'bg-rose-50 dark:bg-rose-950/30 border-rose-100 dark:border-rose-900/20 text-rose-700 dark:text-rose-400'
                                      : 'bg-gray-50 dark:bg-slate-900 border-gray-200 dark:border-slate-800 text-gray-500 dark:text-gray-400'
                              }`}>
                              <div className={`w-1.5 h-1.5 rounded-full mr-2 ${app.status === 'placed'
                                ? 'bg-[var(--placement-primary-light)]0 animate-pulse'
                                : app.status === 'offered'
                                  ? 'bg-[var(--placement-primary-light)]0'
                                  : (app.status === 'interviewing' || app.status === 'interview')
                                    ? 'bg-amber-500 animate-pulse'
                                    : (app.status === 'applied' || app.status === 'shortlisted')
                                      ? 'bg-violet-500'
                                      : app.status === 'rejected'
                                        ? 'bg-rose-500'
                                        : 'bg-gray-400'
                                }`} />
                              {app.status}
                            </div>
                          </td>
                          <td className="py-6 text-right pr-8">
                            <div className="flex items-center justify-end space-x-2">
                              <div className="text-right mr-4">
                                <p className="text-sm font-black text-gray-900 dark:text-white">{app.offeredPackage || app.jobPosting?.salaryPackage || "--"}</p>
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Package</p>
                              </div>
                              <button className="p-2 text-gray-400 hover:text-[var(--placement-primary)] hover:bg-gray-100 dark:hover:bg-[var(--placement-primary-hover)]/20 rounded-xl transition-all">
                                <Settings className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="py-24 text-center">
                          <div className="max-w-xs mx-auto">
                            <div className="w-16 h-16 bg-gray-50 dark:bg-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6 border border-gray-100 dark:border-[var(--placement-primary-border)]/20 shadow-inner">
                              <GradCap className="w-8 h-8 text-gray-300 dark:text-slate-600" />
                            </div>
                            <h4 className="text-gray-900 dark:text-white font-bold">No Students Tracked</h4>
                            <p className="text-gray-400 dark:text-gray-500 text-sm mt-1 leading-relaxed">Start placing students manually to build your career register.</p>
                            <Button variant="outline" size="sm" className="mt-6 border-gray-200" onClick={() => setIsEnrollModalOpen(true)}>Begin Placement</Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile View: Dynamic Card List for smaller screens */}
              <div className="grid grid-cols-1 gap-4 md:hidden p-4">
                {applications.length > 0 ? (
                  applications.map((app) => (
                    <div
                      key={app._id}
                      className="p-5 bg-white dark:bg-[#001f40]/40 rounded-xl border border-gray-100 dark:border-[var(--placement-primary-border)]/30 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                    >
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#002A5C] to-[#1a3884] text-white flex items-center justify-center font-black text-sm shadow-md flex-shrink-0">
                            {app.student?.fullName ? app.student.fullName.charAt(0) : '?'}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 dark:text-white text-sm leading-snug">
                              {app.student?.fullName || 'N/A Student'}
                            </h3>
                            <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-gray-500 dark:text-gray-400 font-bold uppercase tracking-widest mt-1">
                              <span className="text-[var(--placement-primary)] bg-[var(--placement-primary)]/10 dark:bg-[var(--placement-primary)]/30 px-1.5 py-0.5 rounded leading-none">
                                {app.student?.rollNumber || 'No ID'}
                              </span>
                              {app.student?.mobile && <span>• {app.student.mobile}</span>}
                            </div>
                          </div>
                        </div>

                        <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${app.status === 'placed'
                          ? 'bg-[var(--placement-primary-light)] border-[var(--placement-primary-border)] text-[var(--placement-primary)] dark:bg-[var(--placement-primary-light)] dark:text-[var(--placement-primary)] dark:border-[var(--placement-primary-border)]'
                          : app.status === 'offered'
                            ? 'bg-[var(--placement-primary-light)] border-[var(--placement-primary-border)] text-[var(--placement-primary)] dark:bg-[var(--placement-primary-light)] dark:text-[var(--placement-primary)] dark:border-[var(--placement-primary-border)]'
                            : app.status === 'rejected'
                              ? 'bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-950/30 dark:text-rose-400 dark:border-rose-900/30'
                              : 'bg-white border-gray-200 text-gray-500 dark:bg-[#001f40] dark:border-gray-800 dark:text-gray-400'
                          }`}>
                          {app.status}
                        </div>
                      </div>

                      <div className="space-y-1.5 mb-4 p-3 bg-gray-50 dark:bg-[#001f40]/55 rounded-lg border border-gray-100/50 dark:border-gray-800/40">
                        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Opportunity</p>
                        <p className="font-bold text-gray-800 dark:text-gray-200 text-xs sm:text-sm tracking-tight">{app.jobPosting?.title}</p>
                        <div className="flex items-center text-[10px] text-[var(--placement-primary)] font-black uppercase tracking-wider">
                          <Building2 className="w-3.5 h-3.5 mr-1" />
                          {app.jobPosting?.company?.name}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t border-gray-150 dark:border-gray-800">
                        <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Package Offered</p>
                          <p className="text-sm font-black text-gray-900 dark:text-white">
                            {(() => {
                              const pkg = app.offeredPackage || app.jobPosting?.salaryPackage;
                              if (!pkg) return '--';
                              const s = pkg.toString();
                              return /lpa|month|year|unpaid/i.test(s) ? s : `${s} LPA`;
                            })()}
                          </p>
                        </div>
                        <button className="p-2 text-gray-400 hover:text-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)]/10 dark:hover:bg-[var(--placement-primary-hover)]/20 rounded-xl transition-all">
                          <Settings className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-16 text-center">
                    <div className="w-16 h-16 bg-gray-50 dark:bg-[#001f40] rounded-2xl flex items-center justify-center mx-auto mb-6">
                      <GradCap className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                    </div>
                    <h4 className="text-gray-900 dark:text-white font-bold">No Students Tracked</h4>
                    <p className="text-gray-400 text-xs mt-1">Start placing students manually to build your career register.</p>
                  </div>
                )}
              </div>
            </Card>
          )}
        </motion.div>
      </AnimatePresence>


      {/* View Company Details Modal */}
      {viewCompany && (
        <CompanyDetailsModal
          isOpen={!!viewCompany}
          onClose={() => setViewCompany(null)}
          company={viewCompany}
        />
      )}

      {/* Modern Job Modal */}
      {isJobModalOpen && (
        <JobModal
          isOpen={isJobModalOpen}
          onClose={() => { setIsJobModalOpen(false); setSelectedJob(null); }}
          job={selectedJob}
          companies={allCompanies}
          onSuccess={fetchAllData}
        />
      )}

      {/* Same details view the recruiter pages use, so both stay in sync */}
      <JobDetailsModal
        job={viewJobDetails}
        onClose={() => setViewJobDetails(null)}
        onEdit={(j) => { setSelectedJob(j); setIsJobModalOpen(true); }}
        jobFairTitle={jobFairs.find(f => f._id === (viewJobDetails?.jobFairId?._id || viewJobDetails?.jobFairId))?.title}
      />

      {/* Job Fair Modal */}
      {isJobFairModalOpen && (
        <JobFairModal
          isOpen={isJobFairModalOpen}
          onClose={() => setIsJobFairModalOpen(false)}
          fair={selectedJobFair}
          companies={companies}
          recruiters={recruiters}
          onSuccess={() => { setIsJobFairModalOpen(false); fetchAllData(); }}
        />
      )}

      {/* Modern Enrollment Modal */}
      {isEnrollModalOpen && (
        <EnrollmentModal
          isOpen={isEnrollModalOpen}
          onClose={() => setIsEnrollModalOpen(false)}
          jobs={jobs}
          onSuccess={() => { setIsEnrollModalOpen(false); fetchAllData(); }}
        />
      )}

      {/* Custom Confirm Status Modal */}
      {confirmStatusModal.isOpen && (
        <Modal
          isOpen={confirmStatusModal.isOpen}
          onClose={() => setConfirmStatusModal({ isOpen: false, appId: null, status: null, appData: null })}
          title="Confirm Status Change"
          size={['Shortlisted', 'Placed', 'Selected', 'Offered'].includes(confirmStatusModal.status) ? 'lg' : 'sm'}
        >
          <div className="space-y-6 text-center py-2 font-sans">
            <div className="mx-auto w-16 h-16 bg-amber-50 dark:bg-amber-950/20 rounded-full flex items-center justify-center border border-amber-200 dark:border-amber-900/30">
              <AlertCircle className="w-8 h-8 text-amber-500" />
            </div>
            <div>
              <h4 className="text-gray-900 dark:text-white font-bold text-lg">Change Application Status?</h4>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 leading-relaxed">
                Are you sure you want to update this candidate's application status to <span className="font-semibold text-[var(--placement-primary)]">"{confirmStatusModal.status}"</span>?
              </p>
            </div>
            
            {['Shortlisted', 'Placed', 'Selected', 'Offered'].includes(confirmStatusModal.status) && (
              <div className="mt-4 text-left border border-indigo-100 dark:border-indigo-900/30 rounded-2xl bg-indigo-50/50 dark:bg-[#001f40]/50 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Wand2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <h5 className="font-bold text-gray-800 dark:text-white text-sm">AI Email Draft</h5>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-700 dark:text-gray-300 font-semibold">
                    <input 
                      type="checkbox" 
                      checked={sendEmailWithUpdate} 
                      onChange={(e) => setSendEmailWithUpdate(e.target.checked)}
                      className="rounded text-indigo-600 focus:ring-indigo-500 border-gray-300 bg-white"
                    />
                    Send Email Notification
                  </label>
                </div>
                
                {sendEmailWithUpdate && (
                  <div className="space-y-3">
                    {aiDraftLoading ? (
                      <div className="flex flex-col items-center justify-center py-6">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-500 mb-2" />
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Drafting personalized email...</p>
                      </div>
                    ) : (
                      <>
                        <label className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest pl-1 block">Customize Email Body</label>
                        <textarea
                          value={aiDraftMessage}
                          onChange={(e) => setAiDraftMessage(e.target.value)}
                          className="w-full h-40 p-4 text-sm bg-white dark:bg-[#00152E] text-gray-800 dark:text-gray-200 rounded-xl border border-gray-200 dark:border-[var(--placement-primary-border)]/30 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none shadow-inner"
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-center pt-2">
              <button
                onClick={() => setConfirmStatusModal({ isOpen: false, appId: null, status: null, appData: null })}
                className="px-6 py-2.5 border border-gray-200 dark:border-[var(--placement-primary-border)]/40 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-[var(--placement-primary-hover)]/20 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const { appId, status } = confirmStatusModal;
                  setConfirmStatusModal({ isOpen: false, appId: null, status: null, appData: null });
                  try {
                    const payload = { status };
                    if (sendEmailWithUpdate && aiDraftMessage) {
                      payload.customMessage = aiDraftMessage;
                    }
                    const { data } = await api.put(`/placements/applications/${appId}/status`, payload);
                    if (data.success) {
                      toast.success(`Status updated to ${status}`);
                      fetchAllData();
                    }
                  } catch (error) {
                    console.error('Failed to update status:', error);
                    toast.error(error.response?.data?.message || 'Failed to update status');
                  }
                }}
                className="px-6 py-2.5 bg-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)] text-white font-semibold rounded-xl transition-all shadow-md shadow-[var(--placement-primary-border)] hover:shadow-lg hover:shadow-[var(--placement-primary-border)] transform hover:-translate-y-0.5"
              >
                Confirm
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Bulk Confirm Status Modal */}
      {bulkConfirmModal.isOpen && (
        <Modal
          isOpen={bulkConfirmModal.isOpen}
          onClose={() => setBulkConfirmModal({ isOpen: false, status: null })}
          title="Confirm Bulk Status Change"
          size={['Shortlisted', 'Placed', 'Selected', 'Offered'].includes(bulkConfirmModal.status) ? 'lg' : 'sm'}
        >
          <div className="space-y-6 text-center py-2 font-sans">
            <div className="mx-auto w-16 h-16 bg-amber-50 dark:bg-amber-950/20 rounded-full flex items-center justify-center border border-amber-200 dark:border-amber-900/30">
              <AlertCircle className="w-8 h-8 text-amber-500" />
            </div>
            <div>
              <h4 className="text-gray-900 dark:text-white font-bold text-lg">Update {selectedStudents.length} Students?</h4>
              <p className="text-gray-500 dark:text-gray-400 text-sm mt-2 leading-relaxed">
                Are you sure you want to update <span className="font-semibold text-gray-900 dark:text-white">{selectedStudents.length} selected candidates</span> to <span className="font-semibold text-[var(--placement-primary)]">"{bulkConfirmModal.status}"</span>?
              </p>
            </div>
            
            {['Shortlisted', 'Placed', 'Selected', 'Offered'].includes(bulkConfirmModal.status) && (
              <div className="mt-4 text-left border border-indigo-100 dark:border-indigo-900/30 rounded-2xl bg-indigo-50/50 dark:bg-[#001f40]/50 p-5 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Wand2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                    <h5 className="font-bold text-gray-800 dark:text-white text-sm">AI Bulk Email Draft</h5>
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer text-gray-700 dark:text-gray-300 font-semibold">
                    <input 
                      type="checkbox" 
                      checked={sendEmailWithUpdate} 
                      onChange={(e) => setSendEmailWithUpdate(e.target.checked)}
                      className="rounded text-indigo-600 focus:ring-indigo-500 border-gray-300 bg-white"
                    />
                    Send Email Notifications
                  </label>
                </div>
                
                {sendEmailWithUpdate && (
                  <div className="space-y-3">
                    {aiDraftLoading ? (
                      <div className="flex flex-col items-center justify-center py-6">
                        <Loader2 className="w-6 h-6 animate-spin text-indigo-500 mb-2" />
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Drafting personalized bulk email template...</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium mb-1">
                          Note: The system will automatically replace <span className="font-bold">"[Student Name]"</span> with each candidate's actual name when sending.
                        </p>
                        <label className="text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest pl-1 block">Customize Template Body</label>
                        <textarea
                          value={aiDraftMessage}
                          onChange={(e) => setAiDraftMessage(e.target.value)}
                          className="w-full h-40 p-4 text-sm bg-white dark:bg-[#00152E] text-gray-800 dark:text-gray-200 rounded-xl border border-gray-200 dark:border-[var(--placement-primary-border)]/30 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none shadow-inner"
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 justify-center pt-2">
              <button
                onClick={() => setBulkConfirmModal({ isOpen: false, status: null })}
                className="px-6 py-2.5 border border-gray-200 dark:border-[var(--placement-primary-border)]/40 dark:text-gray-300 rounded-xl text-sm font-semibold hover:bg-gray-50 dark:hover:bg-[var(--placement-primary-hover)]/20 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const { status } = bulkConfirmModal;
                  setBulkConfirmModal({ isOpen: false, status: null });
                  try {
                    toast.loading(`Updating ${selectedStudents.length} candidates...`, { id: 'bulkUpdate' });
                    // Bulk update loop
                    const promises = selectedStudents.map(appId => {
                      const payload = { status };
                      if (sendEmailWithUpdate && aiDraftMessage) {
                        // find student name to replace placeholder
                        const app = applications.find(a => a._id === appId);
                        const sName = app?.student?.fullName || app?.studentName || 'Student';
                        payload.customMessage = aiDraftMessage.replace(/\[Student Name\]/gi, sName);
                      }
                      return api.put(`/placements/applications/${appId}/status`, payload);
                    });
                    await Promise.all(promises);
                    toast.success(`Successfully updated ${selectedStudents.length} candidates`, { id: 'bulkUpdate' });
                    setSelectedStudents([]);
                    fetchAllData();
                  } catch (error) {
                    console.error('Failed to bulk update status:', error);
                    toast.error(error.response?.data?.message || 'Failed to complete bulk update', { id: 'bulkUpdate' });
                  }
                }}
                className="px-6 py-2.5 bg-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)] text-white font-semibold rounded-xl transition-all shadow-md shadow-[var(--placement-primary-border)] hover:shadow-lg hover:shadow-[var(--placement-primary-border)] transform hover:-translate-y-0.5"
              >
                Confirm Updates
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Presentation Components
// ---------------------------------------------------------------------

const GlassStatsCard = ({ title, value, icon: Icon, badgeText, badgeColor = 'emerald' }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const badgeStyles = {
    emerald: 'bg-[var(--placement-primary-light)] text-[var(--placement-primary)] dark:bg-[var(--placement-primary-light)] dark:text-[var(--placement-primary)]',
    blue: 'bg-[var(--placement-primary-light)] text-[var(--placement-primary)] dark:bg-[var(--placement-primary-light)] dark:text-[var(--placement-primary)]',
    purple: 'bg-purple-50 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
    rose: 'bg-rose-50 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
  };

  return (
    <motion.div
      whileHover={{ y: -2 }}
      className={`relative overflow-hidden border border-gray-100/60 dark:border-[var(--placement-primary-border)]/30 rounded-[1rem] p-4 shadow-sm hover:shadow-md transition-all duration-300 flex items-center justify-between gap-3 ${isDark ? 'bg-[#002147]' : 'bg-white'}`}
    >
      <div className="flex items-center gap-3.5 overflow-hidden">
        <div className="h-11 w-11 rounded-xl bg-[var(--placement-primary)] flex shrink-0 items-center justify-center text-white shadow-sm">
          <Icon className="h-5 w-5" />
        </div>
        
        <div className="text-left overflow-hidden">
          <h3 className="text-2xl font-black leading-none tracking-tight text-gray-900 dark:text-white">
            {value !== undefined && value !== null ? value : 0}
          </h3>
          <p className="mt-1 text-[11px] font-semibold text-gray-500 dark:text-gray-400 truncate">
            {title}
          </p>
        </div>
      </div>

      {badgeText && (
        <div className="shrink-0">
          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide ${badgeStyles[badgeColor] || badgeStyles.emerald}`}>
            {badgeText}
          </span>
        </div>
      )}
    </motion.div>
  );
};

const ProgressMetric = ({ label, percent, color }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <span className="text-sm font-bold text-gray-700 dark:text-gray-300">{label}</span>
      <span className="text-sm font-black text-[var(--placement-primary)]">{percent}%</span>
    </div>
    <div className="w-full h-2.5 bg-gray-100 dark:bg-[#001f40] rounded-full overflow-hidden border border-white dark:border-[var(--placement-primary-border)]/20">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${percent}%` }}
        transition={{ duration: 1, ease: "easeOut" }}
        className={`h-full ${color} rounded-full shadow-lg shadow-current/20`}
      />
    </div>
  </div>
);

const EmptyState = ({ icon: Icon, title, desc, action, actionLabel }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div className={`col-span-full py-20 text-center backdrop-blur-sm rounded-[2rem] border border-dashed border-gray-300 dark:border-[var(--placement-primary-border)]/40 ${isDark ? 'bg-[#002147]/50' : 'bg-white/50'}`}>
      <div className={`w-20 h-20 shadow-xl border rounded-3xl flex items-center justify-center mx-auto mb-6 transform hover:rotate-12 transition-transform duration-500 ${isDark ? 'bg-[#001f40]' : 'bg-white'} border-gray-100 dark:border-[var(--placement-primary-border)]/30`}>
        <Icon className="w-10 h-10 text-blue-200 dark:text-[var(--placement-primary)]/60" />
      </div>
      <h3 className="text-2xl font-display font-bold text-gray-900 dark:text-white tracking-tight">{title}</h3>
      <p className="text-gray-500 dark:text-gray-400 mt-2 max-w-sm mx-auto font-medium leading-relaxed">{desc}</p>
      {action && (
        <Button
          variant="outline"
          icon={Plus}
          onClick={action}
          className={`mt-8 border rounded-xl px-8 py-3 hover:bg-[var(--placement-primary-hover)] hover:text-white dark:hover:bg-[var(--placement-primary)] dark:hover:text-white transition-all shadow-xl ${isDark ? 'border-[var(--placement-primary-border)]/40 bg-[#001f40] text-gray-250 shadow-none' : 'border-gray-200 bg-white shadow-gray-100'}`}
        >
          {actionLabel}
        </Button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------
// Sub-components Modals (revamped with placeholders)
// ---------------------------------------------------------------------

const CompanyDetailsModal = ({ isOpen, onClose, company }) => {
  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in lg:left-64" onClick={onClose}>
      <div className="relative bg-white dark:bg-[#00152E] rounded-[1.5rem] shadow-2xl flex flex-col w-full max-w-[500px] overflow-hidden border border-slate-100 dark:border-[#1a3884]/20" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800 flex-shrink-0 bg-white dark:bg-[#00152E]">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-slate-50 dark:bg-[#002147] border border-slate-200/60 dark:border-slate-700/50 flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
              {company.logo ? (
                <img src={company.logo} alt="Logo" className="w-full h-full object-cover" />
              ) : (
                <Building2 className="w-6 h-6 text-slate-400 dark:text-slate-500" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {company.name}
              </h2>
              {company.industry && (
                <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mt-0.5">
                  {company.industry}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-white rounded-lg transition-colors absolute top-4 right-4 bg-slate-50 dark:bg-slate-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(85vh-120px)] custom-scrollbar bg-white dark:bg-[#00152E]">
          
          {company.description && (
            <div className="space-y-2">
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                About Company
              </label>
              <div className="p-4 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800">
                <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {company.description}
                </p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {company.website && (
              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                  Website
                </label>
                <a href={company.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-[#4f7dff] hover:underline p-3 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800 break-all">
                  <Globe className="w-4 h-4 shrink-0" />
                  {company.website}
                </a>
              </div>
            )}
            
            {company.contactEmail && (
              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                  Contact Mail
                </label>
                <a href={`mailto:${company.contactEmail}`} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 p-3 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800 break-all">
                  <Mail className="w-4 h-4 shrink-0 text-slate-400" />
                  {company.contactEmail}
                </a>
              </div>
            )}

            {company.contactPhone && (
              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                  Phone Number
                </label>
                <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 p-3 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800">
                  <Phone className="w-4 h-4 shrink-0 text-slate-400" />
                  {company.contactPhone}
                </div>
              </div>
            )}

            {company.registrationNumber && (
              <div className="space-y-1">
                <label className="block text-[10px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                  {company.registrationNumber.length === 21 ? 'CIN Number' : company.registrationNumber.length === 15 ? 'GSTIN Number' : 'Registration Number'}
                </label>
                <div className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 p-3 bg-slate-50 dark:bg-[#002147] rounded-xl border border-slate-100 dark:border-slate-800">
                  <Briefcase className="w-4 h-4 shrink-0 text-slate-400" />
                  {company.registrationNumber}
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

const parseSalary = (raw, jobType = 'Full-time') => {
  if (!raw) return { amount: '', period: jobType === 'Internship' ? '/ Month' : 'LPA' };
  const s = String(raw).trim();
  if (/unpaid/i.test(s)) return { amount: '', period: 'Unpaid' };
  if (/month|mo\b/i.test(s)) {
    return { amount: s.replace(/₹|\/|\s*month|\s*mo\b/gi, '').trim(), period: '/ Month' };
  }
  if (/year|annum|per annum/i.test(s)) {
    return { amount: s.replace(/₹|\/|\s*year|\s*annum/gi, '').trim(), period: '/ Year' };
  }
  return { amount: s.replace(/₹|\s*lpa/gi, '').trim(), period: 'LPA' };
};

const formatSalary = (amount, period) => {
  if (period === 'Unpaid') return 'Unpaid';
  const cleaned = String(amount || '').trim();
  if (!cleaned) return '';
  if (period === 'LPA') return `${cleaned} LPA`;
  if (period === '/ Month') return `₹ ${cleaned} / Month`;
  if (period === '/ Year') return `₹ ${cleaned} / Year`;
  return `${cleaned} ${period}`;
};

const JobModal = ({ isOpen, onClose, job, companies, onSuccess }) => {
  const [formData, setFormData] = useState({
    company: '',
    title: '',
    salaryAmount: '',
    salaryPeriod: 'LPA',
    salaryPackage: '',
    location: '',
    status: 'open',
    jobType: 'Full-time',
    workMode: 'Work from Office',
    deadline: '',
    minCGPA: 0,
    allowedDegrees: '',
    allowedBranches: '',
    isOpenToAll: false,
    description: '',
    benefits: '',
    jdDocument: '',
    jdDocumentName: '',
    skills: '',
    experience: '',
    noBacklog: false,
    hasMin12th: false,
    min12thType: 'both',
    min12thPercentage: '',
    min12thCGPA: '',
    hasMin10th: false,
    min10thType: 'both',
    min10thPercentage: '',
    min10thCGPA: '',
    structuredSkills: [],
    projectDomains: [],
    courseSkills: [],
    openingDate: ''
  });

  // Shared catalog — fetched once per session, cascaded degree -> specialisation.
  const { getDegreeOptions, getDegreeLabel, getSpecializationsForDegrees } = useDegrees();
  const [allSkills, setAllSkills] = useState([]);
  const [degreeSearch, setDegreeSearch] = useState('');
  const [showDegreeDropdown, setShowDegreeDropdown] = useState(false);
  const [branchSearch, setBranchSearch] = useState('');
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);
  const [skillSearch, setSkillSearch] = useState('');
  const [showSkillDropdown, setShowSkillDropdown] = useState(false);
  const [showSalaryDropdown, setShowSalaryDropdown] = useState(false);
  const [salarySearch, setSalarySearch] = useState('');
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [confirmDeleteSkillIdx, setConfirmDeleteSkillIdx] = useState(null);
  const [locationSearch, setLocationSearch] = useState('');

  const selectedLocations = useMemo(() => {
    if (!formData.location) return [];
    if (Array.isArray(formData.location)) return formData.location.filter(Boolean);
    return formData.location.split(',').map(s => s.trim()).filter(Boolean);
  }, [formData.location]);

  const handleToggleLocation = (city) => {
    const trimmed = city.trim();
    if (!trimmed) return;
    const current = (formData.location ? (Array.isArray(formData.location) ? formData.location : formData.location.split(',').map(s => s.trim()).filter(Boolean)) : []);
    const exists = current.some(c => c.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      const updated = current.filter(c => c.toLowerCase() !== trimmed.toLowerCase());
      setFormData(prev => ({ ...prev, location: updated.join(', ') }));
    } else {
      if (current.length >= 5) {
        toast.error('Maximum 5 locations allowed');
        return;
      }
      const updated = [...current, trimmed];
      setFormData(prev => ({ ...prev, location: updated.join(', ') }));
    }
  };

  const handleRemoveLocation = (locToRemove, e) => {
    e?.stopPropagation?.();
    const current = (formData.location ? (Array.isArray(formData.location) ? formData.location : formData.location.split(',').map(s => s.trim()).filter(Boolean)) : []);
    const updated = current.filter(c => c.toLowerCase() !== locToRemove.toLowerCase());
    setFormData(prev => ({ ...prev, location: updated.join(', ') }));
  };

  /* ── Skill picker performance ──────────────────────────────────────────
     The library holds ~2,600 skills. This list used to be filtered twice on
     every render (lowercasing every name each time), and every checkbox tick
     re-renders the whole form. Lowercase once up front, defer the query so
     typing stays responsive, and stop scanning once enough matches are found. */
  const SKILL_RESULT_LIMIT = 60;
  const deferredSkillSearch = useDeferredValue(skillSearch);

  const indexedSkills = useMemo(
    () => (allSkills || [])
      .filter(s => s?.name)
      .map(s => ({ ...s, _lower: s.name.toLowerCase() })),
    [allSkills]
  );

  // The uploaded role library maps each of its 1,000 roles to a skill set.
  // When the typed job title IS one of those roles, its skills belong at the
  // top of the picker — without this the dropdown was an alphabetical dump
  // starting at "360-Degree Video Integration" no matter what the job was.
  // roleSkillsLibrary.js is the primary source (all 15 skills per role);
  // the DB is a fallback only for roles not yet in the local file.
  const matchedRole = useMemo(() => findRoleByExactTitle(formData.title), [formData.title]);
  const [dbRole, setDbRole] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      const role = await fetchRoleSkillsByTitle(formData.title);
      if (!cancelled) setDbRole(role);
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [formData.title]);

  const recommendedRoleName = dbRole?.roleName || matchedRole?.roleName || '';

  const recommendedSkillNames = useMemo(() => {
    if (dbRole?.skills?.length) {
      return new Set(dbRole.skills.map(s => s.skill_name.toLowerCase()));
    }
    if (!matchedRole) return new Set();
    return new Set(roleToStructuredSkills(matchedRole).map(s => s.skill_name.toLowerCase()));
  }, [dbRole, matchedRole]);

  // Flat list, role-recommended skills first; recommendedCount marks where the
  // "All skills" divider goes in the render.
  const { visibleSkills, recommendedCount } = useMemo(() => {
    const q = deferredSkillSearch.trim().toLowerCase();
    const recommended = [];
    const others = [];
    for (const skill of indexedSkills) {
      if (q && !skill._lower.includes(q)) continue;
      if (recommendedSkillNames.has(skill._lower)) {
        recommended.push(skill);           // never capped — a role has ~15
      } else if (others.length < SKILL_RESULT_LIMIT) {
        others.push(skill);
      }
    }
    return {
      visibleSkills: [...recommended, ...others.slice(0, Math.max(0, SKILL_RESULT_LIMIT - recommended.length))],
      recommendedCount: recommended.length
    };
  }, [indexedSkills, deferredSkillSearch, recommendedSkillNames]);

  // O(1) membership instead of scanning the selected array per rendered row.
  const selectedSkillNames = useMemo(
    () => new Set((formData.structuredSkills || []).map(s => s.skill_name)),
    [formData.structuredSkills]
  );
  
  const [isParsingJd, setIsParsingJd] = useState(false);
  const [roleSuggestions, setRoleSuggestions] = useState([]);
  const [showRoleSuggestions, setShowRoleSuggestions] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fileInputRef = React.useRef(null);

  /**
   * Load the library's full skill set for a role when the typed title matches one.
   * Previously this only happened if the user clicked a suggestion, so a job
   * titled "Flutter Developer" could save with 3 hand-added skills instead of
   * the 15 the library defines — leaving applicants barely scoreable.
   * Existing skills are preserved; only missing ones are added.
   */
  const applyRoleSkillsForTitle = React.useCallback(async (title) => {
    const role = findRoleByExactTitle(title);
    // Uploaded collection first; static file only as fallback.
    const uploaded = await fetchRoleSkillsByTitle(title);
    if (!uploaded && !role) return;

    const libSkills = uploaded?.skills?.length
      ? dbRoleToStructuredSkills(uploaded)
      : roleToStructuredSkills(role);
    const roleName = uploaded?.roleName || role?.roleName;
    setFormData(prev => {
      const current = prev.structuredSkills || [];
      const have = new Set(current.map(s => String(s.skill_name || s.name || '').toLowerCase()));
      const missing = libSkills.filter(s => !have.has(String(s.skill_name).toLowerCase()));
      if (missing.length === 0) return prev;
      toast.success(`Loaded ${missing.length} skill${missing.length === 1 ? '' : 's'} for ${roleName}`);
      return { ...prev, structuredSkills: [...current, ...missing] };
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      api.get('/skillslist').then(res => setAllSkills(res.data?.data || [])).catch(() => {});
    }
  }, [isOpen]);

  const selectedQualifications = formData.allowedDegrees
    ? formData.allowedDegrees.split(',').map(d => d.trim()).filter(Boolean)
    : [];

  const selectedBranches = formData.allowedBranches
    ? formData.allowedBranches.split(',').map(b => b.trim()).filter(Boolean)
    : [];

  // Older jobs were saved with free-text values ("BTECH", "M.Tech.") that are
  // not in the curated catalog. Keep those visible while editing, otherwise the
  // selected value has no checkbox and cannot be unticked.
  const withExisting = (options, selected) =>
    Array.from(new Set([...options, ...selected])).sort();

  // Shows "B.E. (Bachelor of Engineering)" but saves "Bachelor of Engineering",
  // which is exactly what a student record holds — so eligibility matching is a
  // plain equality check rather than a substring guess.
  const qualificationOptions = [
    ...getDegreeOptions(),
    ...selectedQualifications
      .filter(v => !getDegreeOptions().some(o => o.value === v))
      .map(v => ({ value: v, label: v }))
  ];

  // Branches are scoped to the chosen qualifications, so a B.E. role can no
  // longer be tagged with a B.Com. branch. With nothing chosen we fall back to
  // the whole catalog rather than an empty list.
  const uniqueBranches = withExisting(getSpecializationsForDegrees(selectedQualifications), selectedBranches);

  const handleAddManualSkill = () => {
    // Prepend, don't append: with a long skill list the new empty row was
    // landing off-screen at the bottom of the table.
    setFormData(prev => ({
      ...prev,
      structuredSkills: [
        { skill_name: '', requiredLevel: 3, isEssential: true, category: 'Manual' },
        ...prev.structuredSkills
      ]
    }));
  };

  const handleSkillNameChange = (idx, val) => {
    const updated = [...formData.structuredSkills];
    updated[idx].skill_name = val;
    setFormData({ ...formData, structuredSkills: updated });
  };

  const handleSkillLevelChange = (idx, val) => {
    const updated = [...formData.structuredSkills];
    updated[idx].requiredLevel = val;
    setFormData({ ...formData, structuredSkills: updated });
  };

  const removeStructuredSkill = (idx) => {
    const updated = formData.structuredSkills.filter((_, i) => i !== idx);
    setFormData({ ...formData, structuredSkills: updated });
  };

  useEffect(() => {
    if (job) {
      const allowedDegreesStr = job.eligibility?.allowedDegrees ? job.eligibility.allowedDegrees.join(', ') : '';
      const allowedBranchesStr = job.eligibility?.allowedBranches ? job.eligibility.allowedBranches.join(', ') : '';
      const isOpenToAllVal = !job.eligibility?.allowedDegrees || job.eligibility.allowedDegrees.length === 0;
      const skillsStr = job.eligibility?.skills ? job.eligibility.skills.join(', ') : '';
      // Top-level structuredSkills is the persisted location; the nested path is
      // read only for legacy records, then we fall back to reconstructing from
      // eligibility.skills so an existing job never opens with an empty list.
      const structuredSkillsArr = job.structuredSkills?.length
        ? job.structuredSkills
        : job.eligibility?.structuredSkills?.length
          ? job.eligibility.structuredSkills
          : (job.eligibility?.skills || []).map(s => ({ skill_name: s, requiredLevel: 3, isEssential: true }));

      const parsedSal = parseSalary(job.salaryPackage, job.jobType || 'Full-time');

      setFormData({
        company: job.company?._id || '',
        title: job.title || '',
        salaryAmount: parsedSal.amount,
        salaryPeriod: parsedSal.period,
        salaryPackage: job.salaryPackage || '',
        location: job.location || '',
        status: job.status || 'open',
        jobType: job.jobType || 'Full-time',
        workMode: job.workMode || 'Work from Office',
        deadline: job.deadline ? new Date(job.deadline).toISOString().split('T')[0] : '',
        minCGPA: job.eligibility?.minCGPA ? job.eligibility.minCGPA.toString() : '0',
        allowedDegrees: allowedDegreesStr,
        allowedBranches: allowedBranchesStr,
        isOpenToAll: isOpenToAllVal,
        description: job.description || '',
        benefits: job.benefits || '',
        jdDocument: job.jdDocument || '',
        jdDocumentName: job.jdDocumentName || '',
        skills: skillsStr,
        experience: job.experience || '',
        noBacklog: job.eligibility?.noBacklog || false,
        hasMin12th: job.eligibility?.hasMin12th || false,
        min12thType: 'both',
        min12thPercentage: job.eligibility?.min12thPercentage ? job.eligibility.min12thPercentage.toString() : (job.eligibility?.min12thCGPA ? (job.eligibility.min12thCGPA * 10).toString() : ''),
        min12thCGPA: job.eligibility?.min12thCGPA ? job.eligibility.min12thCGPA.toString() : (job.eligibility?.min12thPercentage ? (parseFloat(job.eligibility.min12thPercentage) / 10).toString() : ''),
        hasMin10th: job.eligibility?.hasMin10th || false,
        min10thType: 'both',
        min10thPercentage: job.eligibility?.min10thPercentage ? job.eligibility.min10thPercentage.toString() : (job.eligibility?.min10thCGPA ? (job.eligibility.min10thCGPA * 10).toString() : ''),
        min10thCGPA: job.eligibility?.min10thCGPA ? job.eligibility.min10thCGPA.toString() : (job.eligibility?.min10thPercentage ? (parseFloat(job.eligibility.min10thPercentage) / 10).toString() : ''),
        structuredSkills: structuredSkillsArr,
        projectDomains: job.projectDomains || [],
        courseSkills: job.courseSkills || [],
        openingDate: (job.openingDate ? new Date(job.openingDate).toISOString().slice(0, 10) : '')
      });
    } else {
      setFormData({
        company: '',
        title: '',
        salaryAmount: '',
        salaryPeriod: 'LPA',
        salaryPackage: '',
        location: '',
        status: 'open',
        jobType: 'Full-time',
        workMode: 'Work from Office',
        deadline: '',
        minCGPA: 0,
        allowedDegrees: '',
        allowedBranches: '',
        isOpenToAll: false,
        description: '',
        benefits: '',
        jdDocument: '',
        jdDocumentName: '',
        skills: '',
        experience: '',
        noBacklog: false,
        hasMin12th: false,
        min12thType: 'both',
        min12thPercentage: '',
        min12thCGPA: '',
        hasMin10th: false,
        min10thType: 'both',
        min10thPercentage: '',
        min10thCGPA: '',
        structuredSkills: [],
    projectDomains: [],
    courseSkills: [],
    openingDate: ''
      });
    }
  }, [job]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size exceeds 5MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          jdDocument: reader.result,
          jdDocumentName: file.name
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.company) {
      toast.error('Please select a Hiring Partner');
      return;
    }
    if (!formData.title.trim()) {
      toast.error('Job title is required');
      return;
    }

    // Posting rules shared with the other job forms.
    const salaryErr = validateSalaryRange(formData.salaryAmount, formData.salaryPeriod);
    if (salaryErr) { toast.error(salaryErr); return; }
    const skillsErr = validateMustHaveSkills(formData.structuredSkills);
    if (skillsErr) { toast.error(skillsErr); return; }
    const dateErr = validateUpcomingDate(formData.status, formData.openingDate);
    if (dateErr) { toast.error(dateErr); return; }

    try {
      setSubmitting(true);

      // Safety net: if the title names a known role but no skills were added,
      // fall back to the library set rather than saving an unscoreable job.
      // dbRole is the uploaded collection's match for the current title (kept
      // fresh by the debounced effect above) — the static file is a fallback.
      let finalStructuredSkills = formData.structuredSkills || [];
      if (finalStructuredSkills.length === 0) {
        if (dbRole?.skills?.length) {
          finalStructuredSkills = dbRoleToStructuredSkills(dbRole);
        } else {
          const role = findRoleByExactTitle(formData.title);
          if (role) finalStructuredSkills = roleToStructuredSkills(role);
        }
      }

      const finalAllowedDegrees = formData.isOpenToAll
        ? []
        : (formData.allowedDegrees ? formData.allowedDegrees.split(',').map(d => d.trim()).filter(Boolean) : []);

      const finalAllowedBranches = formData.isOpenToAll
        ? []
        : (formData.allowedBranches ? formData.allowedBranches.split(',').map(b => b.trim()).filter(Boolean) : []);

      const payload = {
        company: formData.company,
        title: formData.title,
        description: formData.description,
        benefits: formData.benefits || '',
        salaryPackage: formatSalary(formData.salaryAmount, formData.salaryPeriod) || formData.salaryPackage || '',
        location: formData.location,
        experience: formData.experience || undefined,
        status: formData.status,
        jobType: formData.jobType,
        workMode: formData.workMode,
        jdDocument: formData.jdDocument,
        jdDocumentName: formData.jdDocumentName,
        // JobPosting declares structuredSkills at the TOP level. Sending it only
        // under `eligibility` meant Mongoose stripped the undeclared nested path,
        // so college-posted roles saved with no requirements and every applicant
        // came back unscoreable.
        structuredSkills: finalStructuredSkills,
        projectDomains: formData.projectDomains || [],
        courseSkills: formData.courseSkills || [],
        openingDate: formData.status === 'upcoming' && formData.openingDate ? new Date(formData.openingDate) : null,
        eligibility: {
          minCGPA: parseFloat(formData.minCGPA) || 0,
          allowedDegrees: finalAllowedDegrees,
          allowedBranches: finalAllowedBranches,
          skills: finalStructuredSkills.map(s => s.skill_name).filter(Boolean),
          noBacklog: formData.noBacklog || false,
          hasMin12th: formData.hasMin12th || false,
          min12thPercentage: formData.hasMin12th ? (parseFloat(formData.min12thPercentage) || 0) : 0,
          min12thCGPA: formData.hasMin12th ? (parseFloat(formData.min12thCGPA) || (formData.min12thPercentage ? parseFloat(formData.min12thPercentage) / 10 : 0)) : 0,
          min12thType: 'both',
          hasMin10th: formData.hasMin10th || false,
          min10thPercentage: formData.hasMin10th ? (parseFloat(formData.min10thPercentage) || 0) : 0,
          min10thCGPA: formData.hasMin10th ? (parseFloat(formData.min10thCGPA) || (formData.min10thPercentage ? parseFloat(formData.min10thPercentage) / 10 : 0)) : 0,
          min10thType: 'both'
        }
      };

      if (formData.deadline) {
        payload.deadline = formData.deadline;
      }

      const saveRes = job
        ? await api.put(`/placements/jobs/${job._id}`, payload)
        : await api.post('/placements/jobs', payload);

      // Flagged / locked / approved feedback with the exact issues.
      showModerationFeedback(saveRes?.data?.data, { isEdit: !!job });
      onSuccess();
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error(error.response?.data?.message || 'Failed to save job posting');
    } finally {
      setSubmitting(false);
    }
  };

  const handleParseJD = async () => {
    if (!formData.description?.trim()) {
      toast.error('Please enter a job description to parse');
      return;
    }
    setIsParsingJd(true);
    try {
      const response = await api.post('/placements/parse-jd', { jdText: formData.description });
      if (response.data.success) {
        const parsedData = response.data.data;
        const skillsArray = parsedData.structuredSkills || [];
        
        setFormData(prev => {
          const currentSkills = prev.structuredSkills || [];
          const currentSkillNames = currentSkills.map(s => s.skill_name?.toLowerCase());
          
          // filter out duplicates
          const newSkills = skillsArray.filter(s => {
            const name = typeof s === 'string' ? s : (s.skill_name || s.name);
            return name && !currentSkillNames.includes(name.toLowerCase());
          }).map(s => {
            if (typeof s === 'string') return { skill_name: s, requiredLevel: 3, isEssential: true };
            return {
              skill_name: s.skill_name || s.name,
              requiredLevel: s.requiredLevel || 3,
              isEssential: s.isEssential ?? true,
              category: s.category || s.type || 'Extracted'
            };
          });

          return {
            ...prev,
            title: parsedData.title || prev.title,
            description: parsedData.description || prev.description,
            structuredSkills: [...currentSkills, ...newSkills]
          };
        });
        toast.success(`Job description parsed successfully! Extracted ${skillsArray.length} skills.`);
      }
    } catch (error) {
      console.error('Error parsing JD:', error);
      toast.error('Failed to parse Job Description. Please try again or add skills manually.');
    } finally {
      setIsParsingJd(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-fade-in lg:left-64">
      <div className="bg-white dark:bg-[#001e42] w-full max-w-3xl rounded-3xl shadow-[0_32px_80px_rgba(0,0,0,0.4)] overflow-hidden border border-gray-100 dark:border-[#1a3884]/40 animate-scale-in flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 dark:border-[#1a3884]/30 bg-white dark:bg-[#001430] shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-2xl bg-[#1a3884] shadow-lg shadow-[#1a3884]/30 text-white shrink-0">
              {job ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight leading-tight">
                {job ? 'Edit Job Posting' : 'Post New Job Opportunity'}
              </h2>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5 font-medium">
                {job ? 'Update the details of your job listing' : 'Fill in the details below to create a new listing'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-primary-900/20 rounded-xl transition-colors text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 bg-white dark:bg-[#001e42]">
          <div className="p-8 space-y-8 overflow-y-auto custom-scrollbar flex-1 pb-32">

            {/* ===== Section: Basics ===== */}
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <span className="w-1 h-4 rounded-full bg-primary-800 inline-block"></span>
                <h3 className="text-xs font-bold text-primary-800 dark:text-primary-400 uppercase tracking-widest">Basics</h3>
                <div className="flex-1 h-px bg-gradient-to-r from-primary-800/20 to-transparent" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5 relative">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Job Title <span className="text-primary-800">*</span></label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={(e) => {
                      handleFormChange(e);
                      const matches = matchRoleByTitle(e.target.value);
                      setRoleSuggestions(matches);
                      setShowRoleSuggestions(matches.length > 0);
                    }}
                    onFocus={() => {
                      const matches = matchRoleByTitle(formData.title);
                      setRoleSuggestions(matches);
                      setShowRoleSuggestions(matches.length > 0);
                    }}
                    onBlur={() => {
                      setTimeout(() => setShowRoleSuggestions(false), 150);
                      // Typing a known role name loads its full skill set, so the
                      // skills no longer depend on the user clicking a suggestion.
                      applyRoleSkillsForTitle(formData.title);
                    }}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 transition-all"
                    placeholder="E.g. Associate Software Engineer"
                    required
                    autoComplete="off"
                  />
                  {/* Role Suggestions Dropdown */}
                  {showRoleSuggestions && roleSuggestions.length > 0 && (
                      <div className="absolute left-0 right-0 top-full mt-1 bg-white dark:bg-[#001f40] border border-gray-200 dark:border-primary-900/40 rounded-xl shadow-xl z-50 overflow-hidden">
                          <div className="px-3 py-2 border-b border-gray-100 dark:border-primary-900/20 flex items-center gap-1.5">
                              <Zap size={11} className="text-primary-800" />
                              <span className="text-[10px] font-bold text-primary-800 uppercase tracking-wider">SMAART Role Library — Pick to auto-load skills</span>
                          </div>
                          {roleSuggestions.map(role => (
                              <button
                                  key={role.roleId}
                                  type="button"
                                  onMouseDown={async () => {
                                      setShowRoleSuggestions(false);
                                      // Load all 15 skills from roleSkillsLibrary.js (technical + domain + AI tools).
                                      // fetchRoleSkillsByTitle returns the local library first, DB only as fallback.
                                      const loaded = await fetchRoleSkillsByTitle(role.roleName);
                                      const skills = loaded?.skills?.length
                                          ? dbRoleToStructuredSkills(loaded)
                                          : roleToStructuredSkills(role);
                                      setFormData(prev => ({
                                          ...prev,
                                          title: role.roleName,
                                          structuredSkills: skills
                                      }));
                                      toast.success(`✅ Loaded ${skills.length} skills for "${role.roleName}"`);
                                  }}
                                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-primary-900/10 transition-colors border-b border-gray-50 dark:border-primary-900/10 last:border-0"
                              >
                                  <div className="flex items-center justify-between">
                                      <div>
                                          <span className="text-sm font-semibold text-gray-800 dark:text-white">{role.roleName}</span>
                                      </div>
                                      <span className="text-[10px] font-semibold text-primary-800 bg-primary-50 dark:bg-primary-900/20 px-2 py-0.5 rounded-full">{role.jobFamily}</span>
                                  </div>
                              </button>
                          ))}
                      </div>
                  )}
                </div>

                <div className="space-y-1.5 relative">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Job Type <span className="text-primary-800">*</span></label>
                  <select
                    name="jobType"
                    value={formData.jobType}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData(prev => {
                        let newPeriod = prev.salaryPeriod;
                        if (val === 'Internship' && (prev.salaryPeriod === 'LPA' || prev.salaryPeriod === '/ Year' || !prev.salaryPeriod)) {
                          newPeriod = '/ Month';
                        } else if (val !== 'Internship' && (prev.salaryPeriod === '/ Month' || prev.salaryPeriod === '/ Year' || !prev.salaryPeriod)) {
                          newPeriod = 'LPA';
                        }
                        return {
                          ...prev,
                          jobType: val,
                          salaryPeriod: newPeriod,
                          salaryPackage: formatSalary(prev.salaryAmount, newPeriod)
                        };
                      });
                    }}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all cursor-pointer"
                    required
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>

                {/* Hiring Partner — after the Title | Job Type row so the form
                    opens with the same pairing as the recruiter and job-fair
                    forms. */}
                <div className="space-y-1.5 relative">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Hiring Partner <span className="text-primary-800">*</span></label>
                  <select
                    name="company"
                    value={formData.company}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all cursor-pointer"
                  >
                    <option value="">Select an authenticated partner</option>
                    {companies.filter(c => c.college).map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                  </select>
                </div>

                <div className="space-y-1.5 relative">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Work Mode <span className="text-primary-800">*</span></label>
                  <select
                    name="workMode"
                    value={formData.workMode}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all cursor-pointer"
                    required
                  >
                    <option value="Work from Office">Work from Office</option>
                    <option value="Work from Home">Work from Home</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>

                {/* Experience — a candidate requirement, not compensation. This
                    form has five half-width fields (no Company Branch), so
                    Experience takes the full row rather than sitting beside an
                    empty slot. */}
                <div className="md:col-span-2 space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Experience <span className="normal-case font-normal text-gray-400">(Optional)</span></label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 transition-all"
                    placeholder="E.g. Freshers, 1-2 Years"
                  />
                </div>

                <div className="md:col-span-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Roles &amp; Responsibilities</label>
                    <span className={`text-[10px] font-semibold ${(formData.description?.length || 0) >= 1000 ? 'text-rose-500' : 'text-gray-400'}`}>
                      {formData.description?.length || 0}/1000
                    </span>
                  </div>
                  <textarea
                    name="description"
                    rows="4"
                    maxLength={1000}
                    value={formData.description}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 transition-all resize-none leading-relaxed"
                    placeholder="Describe the roles and responsibilities for this position..."
                  ></textarea>
                </div>
                <div className="md:col-span-2 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Benefits &amp; Perks <span className="normal-case font-medium text-gray-400">(Optional)</span>
                    </label>
                    <span className={`text-[10px] font-semibold ${(formData.benefits?.length || 0) >= 500 ? 'text-rose-500' : 'text-gray-400'}`}>
                      {formData.benefits?.length || 0}/500
                    </span>
                  </div>
                  <textarea
                    name="benefits"
                    rows="3"
                    maxLength={500}
                    value={formData.benefits || ''}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 transition-all resize-none leading-relaxed"
                    placeholder="E.g. Health insurance, provident fund, relocation support, learning budget..."
                  ></textarea>
                </div>
              </div>
            </div>

            {/* ===== Project Domains (multi-select) ===== */}
            <div className="pt-1">
              <ProjectDomainPicker
                value={formData.projectDomains || []}
                onChange={(list) => setFormData(prev => ({ ...prev, projectDomains: list }))}
              />
            </div>

            {/* ===== Section: Compensation & Location ===== */}
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <span className="w-1 h-4 rounded-full bg-primary-800 inline-block"></span>
                <h3 className="text-xs font-bold text-primary-800 dark:text-primary-400 uppercase tracking-widest">Compensation & Location</h3>
                <div className="flex-1 h-px bg-gradient-to-r from-primary-800/20 to-transparent" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <div className="flex items-center">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      {formData.jobType === 'Internship' ? 'Stipend' : 'Salary Package'}
                    </label>
                  </div>
                  <div className="relative flex rounded-xl border border-gray-200 dark:border-primary-900/30 focus-within:ring-2 focus-within:ring-primary-800 focus-within:border-transparent bg-gray-50 dark:bg-[#001430] transition-all">
                    {formData.salaryPeriod !== 'Unpaid' && (
                      <div className="pl-3.5 flex items-center pointer-events-none text-gray-400 dark:text-slate-500">
                        <IndianRupee size={15} />
                      </div>
                    )}
                    <input
                      type="text"
                      disabled={formData.salaryPeriod === 'Unpaid'}
                      name="salaryAmount"
                      value={formData.salaryPeriod === 'Unpaid' ? 'Unpaid' : (formData.salaryAmount !== undefined ? formData.salaryAmount : '')}
                      onChange={(e) => {
                        const val = e.target.value;
                        const defaultPeriod = formData.jobType === 'Internship' ? '/ Month' : 'LPA';
                        setFormData(prev => ({
                          ...prev,
                          salaryAmount: val,
                          salaryPackage: formatSalary(val, prev.salaryPeriod || defaultPeriod)
                        }));
                      }}
                      className="min-w-0 flex-1 pl-2.5 pr-3 py-3 rounded-l-xl bg-transparent text-sm font-medium text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 focus:outline-none disabled:text-gray-400 disabled:italic"
                      placeholder={(() => {
                        // Hint follows the chosen UNIT, not the job type — an
                        // internship priced in LPA should prompt lakhs.
                        const period = formData.salaryPeriod || (formData.jobType === 'Internship' ? '/ Month' : 'LPA');
                        if (period === 'Unpaid') return 'Unpaid / Not Disclosed';
                        return period === '/ Month' ? 'e.g. 15,000 - 20,000' : 'e.g. 3 - 5';
                      })()}
                    />
                    <div className="relative w-32 border-l border-gray-200 dark:border-primary-900/30 bg-white dark:bg-[#001f40] rounded-r-xl">
                      <button
                        type="button"
                        onClick={() => setShowSalaryDropdown(prev => !prev)}
                        className="h-full w-full px-3 py-3 text-left text-xs font-black text-gray-800 dark:text-slate-100 focus:outline-none flex items-center justify-between gap-2 rounded-r-xl hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 transition-colors"
                      >
                        <span className="truncate">{formData.salaryPeriod || (formData.jobType === 'Internship' ? '/ Month' : 'LPA')}</span>
                        <ChevronDown size={15} className={`text-gray-500 dark:text-slate-400 shrink-0 transition-transform ${showSalaryDropdown ? 'rotate-180' : ''}`} />
                      </button>
                      {showSalaryDropdown && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowSalaryDropdown(false)} />
                          <div className="absolute right-0 top-full z-50 mt-1 w-40 overflow-hidden rounded-xl border border-gray-200 dark:border-primary-900/40 bg-white dark:bg-[#001f40] shadow-xl">
                            {(formData.jobType === 'Internship'
                              ? [
                                  { value: '/ Month', label: '/ Month', desc: 'Monthly stipend' },
                                  { value: 'LPA', label: 'LPA', desc: 'Lakhs per annum' },
                                  { value: 'Unpaid', label: 'Unpaid', desc: 'No compensation' },
                                ]
                              : [
                                  { value: 'LPA', label: 'LPA', desc: 'Lakhs per annum' },
                                  { value: 'Unpaid', label: 'Unpaid', desc: 'No compensation' },
                                ]
                            ).map(({ value: period, label, desc }) => {
                              const isSelected = (formData.salaryPeriod || (formData.jobType === 'Internship' ? '/ Month' : 'LPA')) === period;
                              return (
                                <button
                                  key={period}
                                  type="button"
                                  onClick={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      salaryPeriod: period,
                                      salaryPackage: formatSalary(prev.salaryAmount, period)
                                    }));
                                    setShowSalaryDropdown(false);
                                  }}
                                  className={`w-full px-4 py-2.5 text-left transition-colors flex items-center justify-between gap-2 ${isSelected ? 'bg-[#1a3884]/10 text-[#1a3884] dark:bg-[#4f7dff]/15 dark:text-[#9bb8ff]' : 'text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-[#1a3884]/20'}`}
                                >
                                  <div>
                                    <div className="text-xs font-black">{label}</div>
                                    <div className="text-[10px] text-gray-400 dark:text-slate-500 font-medium">{desc}</div>
                                  </div>
                                  {isSelected && <CheckCircle2 size={14} className="shrink-0" />}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-400 dark:text-slate-500">Enter a range — e.g. 3 - 5 (a single figure is not accepted).</p>
                </div>
                {/* Location with multi-city picker (Max 5) */}
                <div className="space-y-1.5 relative">
                  <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Location <span className="normal-case font-normal text-gray-400">(Max 5)</span>
                    </label>
                    {selectedLocations.length > 0 && (
                      <span className={`text-[11px] font-bold ${selectedLocations.length >= 5 ? 'text-amber-500' : 'text-[#1a3884] dark:text-[#4f7dff]'}`}>
                        {selectedLocations.length}/5 selected
                      </span>
                    )}
                  </div>
                  <div
                    onClick={() => setShowLocationDropdown(!showLocationDropdown)}
                    className="min-h-[46px] w-full px-3 py-2 border border-gray-200 dark:border-primary-900/30 rounded-xl bg-gray-50 dark:bg-[#001430] text-sm font-medium text-left flex items-center justify-between transition-all cursor-pointer hover:border-[#1a3884]/40"
                  >
                    <div className="flex flex-wrap gap-1.5 flex-1 min-w-0 pr-2">
                      {selectedLocations.length > 0 ? (
                        selectedLocations.map((loc) => (
                          <span
                            key={loc}
                            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold bg-[#1a3884]/10 text-[#1a3884] dark:bg-[#4f7dff]/20 dark:text-[#9bb8ff] border border-[#1a3884]/20 dark:border-[#4f7dff]/30"
                          >
                            <MapPin size={11} className="shrink-0" />
                            <span className="truncate max-w-[120px]">{loc}</span>
                            <button
                              type="button"
                              onClick={(e) => handleRemoveLocation(loc, e)}
                              className="hover:text-red-500 rounded p-0.5 transition-colors"
                            >
                              <X size={12} />
                            </button>
                          </span>
                        ))
                      ) : (
                        <span className="text-gray-400 dark:text-slate-600 flex items-center gap-1.5 text-sm pl-1">
                          <MapPin size={14} className="text-gray-400" />
                          Select up to 5 locations...
                        </span>
                      )}
                    </div>
                    <ChevronDown size={16} className="text-gray-400 dark:text-slate-500 shrink-0" />
                  </div>

                  {showLocationDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowLocationDropdown(false)} />
                      <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-[#001f40] border border-gray-200 dark:border-[#1a3884]/40 rounded-xl shadow-xl py-2 z-50 flex flex-col">
                        <div className="px-3 pb-2 border-b border-gray-100 dark:border-[#1a3884]/20 flex items-center gap-2">
                          <input
                            type="text"
                            value={locationSearch}
                            onChange={(e) => setLocationSearch(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                if (locationSearch.trim()) {
                                  handleToggleLocation(locationSearch.trim());
                                  setLocationSearch('');
                                }
                              }
                            }}
                            placeholder="Search or type custom city & Enter..."
                            autoFocus
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-3 py-1.5 border border-gray-300 dark:border-slate-700 rounded-lg bg-transparent text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-[#1a3884]"
                          />
                          {locationSearch.trim() && !isKnownLocation(locationSearch) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleLocation(locationSearch.trim());
                                setLocationSearch('');
                              }}
                              disabled={selectedLocations.length >= 5 && !selectedLocations.some(c => c.toLowerCase() === locationSearch.trim().toLowerCase())}
                              className="shrink-0 px-2.5 py-1 text-[11px] font-bold bg-[#1a3884] hover:bg-[#122a6b] text-white rounded-lg transition-colors disabled:opacity-50"
                            >
                              + Add
                            </button>
                          )}
                        </div>

                        <div className="px-3 py-1.5 bg-gray-50/70 dark:bg-slate-900/50 flex items-center justify-between text-[11px] text-gray-500 dark:text-slate-400 font-semibold border-b border-gray-100 dark:border-slate-800">
                          <span>{locationSearch.trim() ? 'Matching Locations' : 'Popular Locations'}</span>
                          <span className={selectedLocations.length >= 5 ? 'text-amber-500 font-bold' : ''}>
                            {selectedLocations.length}/5 Selected
                          </span>
                        </div>

                        <div className="overflow-y-auto max-h-48 mt-1 custom-scrollbar">
                          {searchLocations(locationSearch).map(loc => {
 const city = loc.name;
                              const isSelected = selectedLocations.some(c => c.toLowerCase() === city.toLowerCase());
                              const isDisabled = !isSelected && selectedLocations.length >= 5;
                              return (
                                <button
                                  key={city}
                                  type="button"
                                  disabled={isDisabled}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleLocation(city);
                                  }}
                                  className={`w-full text-left px-4 py-2 text-xs font-semibold flex items-center justify-between transition-colors ${
                                    isSelected
                                      ? 'text-[#1a3884] dark:text-[#4f7dff] bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 font-bold'
                                      : isDisabled
                                      ? 'text-gray-300 dark:text-slate-600 cursor-not-allowed'
                                      : 'text-gray-700 dark:text-gray-300 hover:bg-[#1a3884]/5 dark:hover:bg-[#4f7dff]/10 cursor-pointer'
                                  }`}
                                >
                                  <span className="flex items-center gap-2">
                                    <MapPin size={12} className={isSelected ? 'text-[#1a3884] dark:text-[#4f7dff]' : 'text-gray-400'} />
                                    <span className="flex flex-col leading-tight">
                                      <span>{city}</span>
                                      <span className="text-[10px] font-medium text-gray-400">{loc.state}</span>
                                    </span>
                                  </span>
                                  {isSelected ? (
                                    <Check size={14} className="text-[#1a3884] dark:text-[#4f7dff]" />
                                  ) : isDisabled ? (
                                    <span className="text-[10px] text-gray-400">Limit reached</span>
                                  ) : null}
                                </button>
                              );
                            })}
                          {searchLocations(locationSearch).length === 0 && (
                            <div className="text-center py-3 text-xs text-gray-500">
                              No matching city, district or state. Press &quot;+ Add&quot; above to add &quot;{locationSearch}&quot;.
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ===== Section: Eligibility & Benchmarks ===== */}
            {/* flex-col + order puts Qualification/Departments first, then the
                CGPA + benchmark checks — matching the other job forms. */}
            <div className="flex flex-col gap-5">
              <div className="flex items-center gap-3">
                <span className="w-1 h-4 rounded-full bg-primary-800 inline-block"></span>
                <h3 className="text-xs font-bold text-primary-800 dark:text-primary-400 uppercase tracking-widest">Eligibility Benchmarks</h3>
                <div className="flex-1 h-px bg-gradient-to-r from-primary-800/20 to-transparent" />
              </div>
              <div className="order-2 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Minimum CGPA Criteria</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="10"
                    name="minCGPA"
                    value={formData.minCGPA}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 transition-all"
                    placeholder="E.g. 7.5"
                  />
                </div>
                <div className="hidden md:block" />
              </div>

              {/* Benchmarks Details */}
              <div className="order-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 dark:border-primary-900/25 bg-gray-50 dark:bg-[#001430] hover:border-primary-900/40 transition-colors">
                  <input
                    type="checkbox"
                    id="noBacklog"
                    name="noBacklog"
                    checked={formData.noBacklog}
                    onChange={(e) => setFormData(prev => ({ ...prev, noBacklog: e.target.checked }))}
                    className="w-4 h-4 mt-0.5 rounded text-primary-800 focus:ring-primary-800 dark:bg-slate-900 dark:border-slate-700 cursor-pointer"
                  />
                  <div>
                    <label htmlFor="noBacklog" className="text-xs font-semibold text-gray-800 dark:text-gray-200 cursor-pointer select-none">
                      NO ACTIVE BACKLOGS
                    </label>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Student must have zero active backlogs.</p>
                  </div>
                </div>

                {/* 10th Class Percentage & CGPA */}
                <div className="flex flex-col gap-2 p-4 rounded-xl border border-gray-200 dark:border-primary-900/25 bg-gray-50 dark:bg-[#001430] hover:border-primary-900/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="hasMin10th"
                      name="hasMin10th"
                      checked={formData.hasMin10th}
                      onChange={(e) => setFormData(prev => {
                        const checked = e.target.checked;
                        return {
                          ...prev,
                          hasMin10th: checked,
                          min10thPercentage: checked ? (prev.min10thPercentage || '60') : '',
                          min10thCGPA: checked ? (prev.min10thCGPA || (prev.min10thPercentage ? (parseFloat(prev.min10thPercentage) / 10).toFixed(2).replace(/\.00$/, '') : '6.0')) : ''
                        };
                      })}
                      className="w-4 h-4 mt-0.5 rounded text-primary-800 focus:ring-primary-800 dark:bg-slate-900 dark:border-slate-700 cursor-pointer"
                    />
                    <div>
                      <label htmlFor="hasMin10th" className="text-xs font-semibold text-gray-800 dark:text-gray-200 cursor-pointer select-none">
                        10TH CLASS CRITERIA
                      </label>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Require minimum 10th % and CGPA.</p>
                    </div>
                  </div>
                  {formData.hasMin10th && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="block text-[10px] font-semibold text-gray-500 dark:text-slate-400">
                          MIN %
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.min10thPercentage}
                          onChange={(e) => {
                            const val = e.target.value;
                            const num = parseFloat(val);
                            setFormData(prev => ({
                              ...prev,
                              min10thPercentage: val,
                              min10thCGPA: !isNaN(num) && num > 0 ? (num / 10).toFixed(2).replace(/\.00$/, '') : prev.min10thCGPA
                            }));
                          }}
                          className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-primary-900/30 rounded-lg bg-white dark:bg-[#001f40] text-xs dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-800"
                          placeholder="60"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[10px] font-semibold text-gray-500 dark:text-slate-400">
                          MIN CGPA
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.01"
                          value={formData.min10thCGPA}
                          onChange={(e) => {
                            const val = e.target.value;
                            const num = parseFloat(val);
                            setFormData(prev => ({
                              ...prev,
                              min10thCGPA: val,
                              min10thPercentage: !isNaN(num) && num > 0 ? (num * 10).toFixed(1).replace(/\.0$/, '') : prev.min10thPercentage
                            }));
                          }}
                          className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-primary-900/30 rounded-lg bg-white dark:bg-[#001f40] text-xs dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-800"
                          placeholder="6.0"
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* 12th Class Percentage & CGPA */}
                <div className="flex flex-col gap-2 p-4 rounded-xl border border-gray-200 dark:border-primary-900/25 bg-gray-50 dark:bg-[#001430] hover:border-primary-900/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="hasMin12th"
                      name="hasMin12th"
                      checked={formData.hasMin12th}
                      onChange={(e) => setFormData(prev => {
                        const checked = e.target.checked;
                        return {
                          ...prev,
                          hasMin12th: checked,
                          min12thPercentage: checked ? (prev.min12thPercentage || '60') : '',
                          min12thCGPA: checked ? (prev.min12thCGPA || (prev.min12thPercentage ? (parseFloat(prev.min12thPercentage) / 10).toFixed(2).replace(/\.00$/, '') : '6.0')) : ''
                        };
                      })}
                      className="w-4 h-4 mt-0.5 rounded text-primary-800 focus:ring-primary-800 dark:bg-slate-900 dark:border-slate-700 cursor-pointer"
                    />
                    <div>
                      <label htmlFor="hasMin12th" className="text-xs font-semibold text-gray-800 dark:text-gray-200 cursor-pointer select-none">
                        12TH CLASS CRITERIA
                      </label>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">Require minimum 12th % and CGPA.</p>
                    </div>
                  </div>
                  {formData.hasMin12th && (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <label className="block text-[10px] font-semibold text-gray-500 dark:text-slate-400">
                          MIN %
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.min12thPercentage}
                          onChange={(e) => {
                            const val = e.target.value;
                            const num = parseFloat(val);
                            setFormData(prev => ({
                              ...prev,
                              min12thPercentage: val,
                              min12thCGPA: !isNaN(num) && num > 0 ? (num / 10).toFixed(2).replace(/\.00$/, '') : prev.min12thCGPA
                            }));
                          }}
                          className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-primary-900/30 rounded-lg bg-white dark:bg-[#001f40] text-xs dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-800"
                          placeholder="60"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="block text-[10px] font-semibold text-gray-500 dark:text-slate-400">
                          MIN CGPA
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="10"
                          step="0.01"
                          value={formData.min12thCGPA}
                          onChange={(e) => {
                            const val = e.target.value;
                            const num = parseFloat(val);
                            setFormData(prev => ({
                              ...prev,
                              min12thCGPA: val,
                              min12thPercentage: !isNaN(num) && num > 0 ? (num * 10).toFixed(1).replace(/\.0$/, '') : prev.min12thPercentage
                            }));
                          }}
                          className="w-full px-2.5 py-1.5 border border-gray-200 dark:border-primary-900/30 rounded-lg bg-white dark:bg-[#001f40] text-xs dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-800"
                          placeholder="6.0"
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="order-1 grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="relative space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Qualification</label>
                    {selectedQualifications.length > 0 && !formData.isOpenToAll && (
                      <span className="text-[11px] font-semibold text-primary-800 dark:text-blue-400">
                        {selectedQualifications.length} selected
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={formData.isOpenToAll}
                    onClick={() => setShowDegreeDropdown(!showDegreeDropdown)}
                    className={`w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 bg-gray-50 dark:bg-[#001430] text-sm font-medium dark:text-white flex items-center justify-between transition-all
                        ${formData.isOpenToAll ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary-900/50'}`}
                  >
                    <span className="truncate pr-4 text-left text-sm">
                      {formData.isOpenToAll
                        ? 'Open to all qualifications'
                        : (selectedQualifications.map(getDegreeLabel).join(', ') || 'Select Qualifications...')
                      }
                    </span>
                    <ChevronDown size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  </button>

                  {!formData.isOpenToAll && showDegreeDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowDegreeDropdown(false)} />
                      <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-[#001f40] border border-gray-200 dark:border-primary-900/40 rounded-xl shadow-xl py-2 z-50 max-h-64 overflow-y-auto custom-scrollbar flex flex-col">
                        <div className="px-3 pb-2 border-b border-gray-100 dark:border-primary-900/20">
                          <input
                            type="text"
                            value={degreeSearch}
                            onChange={(e) => setDegreeSearch(e.target.value)}
                            placeholder="Search qualification..."
                            className="w-full px-3 py-1.5 border border-gray-300 dark:border-slate-700 rounded-lg bg-transparent text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-800"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="overflow-y-auto max-h-44 mt-1">
                          {qualificationOptions
                            .filter(o => o.label.toLowerCase().includes(degreeSearch.toLowerCase()))
                            .map((opt) => {
                              const qual = opt.value;
                              const selectedArray = formData.allowedDegrees ? formData.allowedDegrees.split(',').map(item => item.trim()).filter(Boolean) : [];
                              const isSelected = selectedArray.includes(qual);
                              return (
                                <label key={qual} className="flex items-center gap-3 px-4 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      let updated;
                                      if (isSelected) updated = selectedArray.filter(item => item !== qual);
                                      else updated = [...selectedArray, qual];
                                      const stillValid = getSpecializationsForDegrees(updated);
                                      setFormData(prev => ({
                                        ...prev,
                                        allowedDegrees: updated.join(', '),
                                        allowedBranches: (prev.allowedBranches || '')
                                          .split(',').map(b => b.trim()).filter(b => b && stillValid.includes(b))
                                          .join(', ')
                                      }));
                                    }}
                                    className="w-4 h-4 rounded text-primary-800 focus:ring-primary-500 dark:bg-slate-900 dark:border-slate-700"
                                  />
                                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{opt.label}</span>
                                </label>
                              );
                            })
                          }
                          {qualificationOptions.filter(o => o.label.toLowerCase().includes(degreeSearch.toLowerCase())).length === 0 && (
                            <div className="text-center py-4 text-xs text-gray-500">No qualifications found</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id="isOpenToAll"
                      name="isOpenToAll"
                      checked={formData.isOpenToAll || false}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          isOpenToAll: e.target.checked,
                          allowedDegrees: e.target.checked ? '' : prev.allowedDegrees,
                          allowedBranches: e.target.checked ? '' : prev.allowedBranches
                        }));
                        if (e.target.checked) {
                          setShowDegreeDropdown(false);
                          setShowBranchDropdown(false);
                        }
                      }}
                      className="w-4 h-4 rounded text-primary-800 focus:ring-primary-500 dark:bg-slate-900 dark:border-slate-700 cursor-pointer"
                    />
                    <label htmlFor="isOpenToAll" className="text-xs font-semibold text-gray-600 dark:text-slate-300 cursor-pointer select-none uppercase tracking-wider">
                      OPEN TO ALL
                    </label>
                  </div>
                </div>

                {/* Branches / Specialization dropdown */}
                <div className="relative space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Departments / Branches
                      {selectedQualifications.length > 0 && !formData.isOpenToAll && (
                        <span className="ml-1.5 normal-case tracking-normal font-semibold text-primary-800 dark:text-blue-400">
                          ({uniqueBranches.length} for {selectedQualifications.map(q => getDegreeLabel(q).split(' (')[0]).join(', ')})
                        </span>
                      )}
                    </label>
                    {formData.allowedBranches && !formData.isOpenToAll && selectedQualifications.length > 0 && (
                      <span className="text-[11px] font-semibold text-primary-800 dark:text-blue-400">
                        {formData.allowedBranches.split(',').filter(Boolean).length} selected
                      </span>
                    )}
                  </div>
                  {(() => {
                    const isBranchDisabled = formData.isOpenToAll || selectedQualifications.length === 0;
                    return (
                      <>
                        <button
                          type="button"
                          disabled={isBranchDisabled}
                          onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                          className={`w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 bg-gray-50 dark:bg-[#001430] text-sm font-medium dark:text-white flex items-center justify-between transition-all
                              ${isBranchDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-slate-900/40 text-gray-400' : 'cursor-pointer hover:border-primary-900/50'}`}
                        >
                          <span className="truncate pr-4 text-left text-sm">
                            {formData.isOpenToAll
                              ? 'Open to all branches'
                              : selectedQualifications.length === 0
                              ? 'Select qualification first'
                              : formData.allowedBranches
                              ? formData.allowedBranches
                              : 'Select Branches...'
                            }
                          </span>
                          <ChevronDown size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
                        </button>

                        {!isBranchDisabled && showBranchDropdown && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setShowBranchDropdown(false)} />
                            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-[#001f40] border border-gray-200 dark:border-primary-900/40 rounded-xl shadow-xl py-2 z-50 max-h-64 overflow-y-auto custom-scrollbar flex flex-col">
                              <div className="px-3 pb-2 border-b border-gray-100 dark:border-primary-900/20">
                                <input
                                  type="text"
                                  value={branchSearch}
                                  onChange={(e) => setBranchSearch(e.target.value)}
                                  placeholder="Search branch..."
                                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-slate-700 rounded-lg bg-transparent text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-800"
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              {/* Select All Branches / Specializations Bar */}
                              <div className="px-3 py-1.5 border-b border-gray-100 dark:border-primary-900/20 flex items-center justify-between bg-gray-50/70 dark:bg-[#001430]/70">
                                {(() => {
                                  const filteredBranches = uniqueBranches.filter(b => b.toLowerCase().includes(branchSearch.toLowerCase()));
                                  const selectedArray = formData.allowedBranches ? formData.allowedBranches.split(',').map(i => i.trim()).filter(Boolean) : [];
                                  const allFilteredSelected = filteredBranches.length > 0 && filteredBranches.every(b => selectedArray.includes(b));
                                  const someFilteredSelected = filteredBranches.some(b => selectedArray.includes(b));
                                  return (
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        ref={input => {
                                          if (input) input.indeterminate = !allFilteredSelected && someFilteredSelected;
                                        }}
                                        onChange={() => {
                                          if (allFilteredSelected) {
                                            const updated = selectedArray.filter(b => !filteredBranches.includes(b));
                                            setFormData(prev => ({ ...prev, allowedBranches: updated.join(', ') }));
                                          } else {
                                            const newSelected = Array.from(new Set([...selectedArray, ...filteredBranches]));
                                            setFormData(prev => ({ ...prev, allowedBranches: newSelected.join(', ') }));
                                          }
                                        }}
                                        className="w-4 h-4 rounded text-primary-800 focus:ring-primary-500 dark:bg-slate-900 dark:border-slate-700 cursor-pointer"
                                      />
                                      <span className="text-xs font-bold text-primary-800 dark:text-blue-400">
                                        {allFilteredSelected ? 'Deselect All' : 'Select All'} ({filteredBranches.length})
                                      </span>
                                    </label>
                                  );
                                })()}
                                {formData.allowedBranches && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFormData(prev => ({ ...prev, allowedBranches: '' }));
                                    }}
                                    className="text-[11px] font-semibold text-rose-500 hover:text-rose-700"
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                              <div className="overflow-y-auto max-h-44 mt-1">
                                {uniqueBranches
                                  .filter(b => b.toLowerCase().includes(branchSearch.toLowerCase()))
                                  .map((branch) => {
                                    const selectedArray = formData.allowedBranches ? formData.allowedBranches.split(',').map(item => item.trim()).filter(Boolean) : [];
                                    const isSelected = selectedArray.includes(branch);
                                    return (
                                      <label key={branch} className="flex items-center gap-3 px-4 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer select-none">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => {
                                            let updated;
                                            if (isSelected) updated = selectedArray.filter(item => item !== branch);
                                            else updated = [...selectedArray, branch];
                                            setFormData(prev => ({ ...prev, allowedBranches: updated.join(', ') }));
                                          }}
                                          className="w-4 h-4 rounded text-primary-800 focus:ring-primary-500 dark:bg-slate-900 dark:border-slate-700"
                                        />
                                        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{branch}</span>
                                      </label>
                                    );
                                  })
                                }
                                {uniqueBranches.filter(b => b.toLowerCase().includes(branchSearch.toLowerCase())).length === 0 && (
                                  <div className="text-center py-4 text-xs text-gray-500">No branches found</div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* ===== Section: Timeline & Drive Status ===== */}
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <span className="w-1 h-4 rounded-full bg-primary-800 inline-block"></span>
                <h3 className="text-xs font-bold text-primary-800 dark:text-primary-400 uppercase tracking-widest">Timeline &amp; Drive Status</h3>
                <div className="flex-1 h-px bg-gradient-to-r from-primary-800/20 to-transparent" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-1.5">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Application Deadline</label>
                  <input
                    type="date"
                    name="deadline"
                    value={formData.deadline}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all"
                  />
                </div>

                <div className="space-y-1.5 relative">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Drive Readiness <span className="text-primary-800">*</span></label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all cursor-pointer"
                  >
                    <option value="open">Recruitment Active (Open)</option>
                    <option value="upcoming">Pipeline Planned (Upcoming)</option>
                    <option value="closed">Drive Terminated (Closed)</option>
                  </select>
                </div>
                {/* Drive Opens On — only for Upcoming. Students cannot see an
                    upcoming job; it auto-opens on this date. */}
                {formData.status === 'upcoming' && (
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Drive Opens On <span className="text-primary-800">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.openingDate || ''}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={(e) => setFormData(prev => ({ ...prev, openingDate: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-800 bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all"
                    />
                    <p className="text-[10px] text-gray-400 dark:text-slate-500">Hidden from students until this date, then it opens automatically.</p>
                  </div>
                )}
              </div>
            </div>

            {/* ===== SMAART Course Skills (multi-select from student courses) ===== */}
            <div className="pt-1">
              <CourseSkillPicker
                value={formData.courseSkills || []}
                onChange={(list) => setFormData(prev => ({ ...prev, courseSkills: list }))}
              />
            </div>

            {/* ===== Section: Skills ===== */}
            <div className="space-y-5">
              <div className="flex items-center gap-3">
                <span className="w-1 h-4 rounded-full bg-primary-800 inline-block"></span>
                <h3 className="text-xs font-bold text-primary-800 dark:text-primary-400 uppercase tracking-widest">Skills & Documents</h3>
                <div className="flex-1 h-px bg-gradient-to-r from-primary-800/20 to-transparent" />
              </div>
              <div className="relative space-y-1.5">
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Required Skills Matrix</label>
                  <button
                    type="button"
                    onClick={handleAddManualSkill}
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#1a3884] dark:text-[#4f7dff] px-3 py-1.5 rounded-lg border border-[#1a3884]/20 dark:border-[#4f7dff]/20 hover:bg-[#1a3884]/5 dark:hover:bg-[#4f7dff]/10 transition-all"
                  >
                    <Plus className="w-3 h-3" />
                    Add Manually
                  </button>
                </div>

                <div className="relative mb-3">
                  <button
                    type="button"
                    onClick={() => setShowSkillDropdown(!showSkillDropdown)}
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-primary-900/30 rounded-xl focus:outline-none focus:ring-2 bg-white dark:bg-[#002147] text-sm dark:text-white flex items-center justify-between transition-colors cursor-pointer focus:ring-primary-800"
                  >
                    <span className="truncate pr-4 text-left text-sm text-gray-500">
                      Select Skills from SMAART Library...
                    </span>
                    <ChevronDown size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  </button>

                  {showSkillDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowSkillDropdown(false)} />
                      <div className="absolute left-0 right-0 bottom-full mb-2 bg-white dark:bg-[#001f40] border border-gray-200 dark:border-primary-900/40 rounded-xl shadow-xl py-2 z-50 max-h-60 overflow-y-auto custom-scrollbar flex flex-col">
                        <div className="px-3 pb-2 border-b border-gray-100 dark:border-primary-900/20">
                          <input
                            type="text"
                            value={skillSearch}
                            onChange={(e) => setSkillSearch(e.target.value)}
                            placeholder="Search skill library..."
                            className="w-full px-3 py-1.5 border border-gray-300 dark:border-slate-700 rounded-lg bg-transparent text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-800"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>

                        <div className="overflow-y-auto max-h-44 mt-1">
                          {recommendedCount === 0 && !skillSearch && visibleSkills.length > 0 && (
                            <div className="px-4 py-2 text-[11px] text-gray-400 bg-gray-50/60 dark:bg-[#001430]/60 border-b border-gray-100 dark:border-primary-900/20">
                              Type a job title above to see that role's skills first — this is the full library A–Z.
                            </div>
                          )}
                          {visibleSkills
                            .map((skill, idx) => {
                              const isSelected = selectedSkillNames.has(skill.name);
                              const isRecommended = idx < recommendedCount;

                              return (
                                <React.Fragment key={skill.id || skill.name}>
                                {idx === 0 && recommendedCount > 0 && (
                                  <div className="px-4 pt-1 pb-1.5 text-[10px] font-bold uppercase tracking-wider text-primary-800 dark:text-blue-300">
                                    Recommended for {recommendedRoleName}
                                  </div>
                                )}
                                {idx === recommendedCount && recommendedCount > 0 && (
                                  <div className="px-4 pt-2 pb-1.5 mt-1 border-t border-gray-100 dark:border-primary-900/20 text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                    All skills
                                  </div>
                                )}
                                <label className={`flex items-center gap-3 px-4 py-1.5 hover:bg-gray-50 dark:hover:bg-[#002A5C]/50 cursor-pointer select-none ${isRecommended ? 'bg-blue-50/50 dark:bg-primary-900/10' : ''}`}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={(e) => {
                                      if (isSelected) {
                                          setFormData(prev => ({ ...prev, structuredSkills: prev.structuredSkills.filter(s => s.skill_name !== skill.name) }));
                                      } else {
                                          setFormData(prev => ({ 
                                              ...prev, 
                                              structuredSkills: [...prev.structuredSkills, { skill_name: skill.name, requiredLevel: 3, isEssential: true }]
                                          }));
                                      }
                                    }}
                                    className="w-4 h-4 rounded text-primary-800 focus:ring-primary-500 border-gray-300 dark:border-slate-600 dark:bg-slate-900"
                                  />
                                  <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{skill.name}</span>
                                </label>
                                </React.Fragment>
                              );
                            })}
                            {visibleSkills.length === 0 && (
                                <div className="p-4 text-center text-xs text-gray-500">
                                  {allSkills.length === 0 ? 'Loading skill library…' : 'No matching skills found in library.'}
                                </div>
                            )}
                            {visibleSkills.length >= SKILL_RESULT_LIMIT && (
                                <div className="px-4 py-2 text-[11px] text-gray-400 border-t border-gray-100 dark:border-primary-900/20">
                                  Showing first {SKILL_RESULT_LIMIT} of {allSkills.length} — type to narrow down
                                </div>
                            )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {formData.structuredSkills.length > 0 && (
                  <div className="border border-gray-100 dark:border-primary-900/20 rounded-xl overflow-hidden bg-white dark:bg-[#001430] shadow-sm">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-100 dark:border-primary-900/20">
                          <th className="pb-2 pt-3 px-4 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider w-[40%]">Skill Name</th>
                          <th className="pb-2 pt-3 px-4 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider w-[25%]">Proficiency</th>
                          <th className="pb-2 pt-3 px-4 text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider text-center w-[25%]">Importance</th>
                          <th className="pb-2 pt-3 px-4 w-[10%]"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50 dark:divide-primary-900/10">
                        {formData.structuredSkills.map((skill, idx) => (
                          <tr key={idx} className="hover:bg-gray-50/50 dark:hover:bg-primary-900/5 transition-colors">
                            <td className="px-4 py-2">
                              <input
                                type="text"
                                value={skill.skill_name}
                                onChange={(e) => handleSkillNameChange(idx, e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border-b border-transparent focus:border-primary-800 focus:outline-none bg-transparent dark:text-white placeholder-gray-400"
                                placeholder="e.g. React"
                              />
                            </td>
                            <td className="px-4 py-2">
                              <select
                                value={skill.requiredLevel || 3}
                                onChange={(e) => handleSkillLevelChange(idx, Number(e.target.value))}
                                className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-primary-900/30 rounded-lg bg-transparent focus:ring-2 focus:ring-primary-800 dark:text-white font-medium"
                              >
                                <option value={1}>1 - Basic</option>
                                <option value={2}>2 - Novice</option>
                                <option value={3}>3 - Intermediate</option>
                                <option value={4}>4 - Advanced</option>
                                <option value={5}>5 - Expert</option>
                              </select>
                            </td>
                            <td className="px-4 py-2 text-center">
                              <select
                                value={skill.isEssential ? 'true' : 'false'}
                                onChange={(e) => {
                                  const updatedSkills = [...formData.structuredSkills];
                                  updatedSkills[idx].isEssential = e.target.value === 'true';
                                  setFormData({ ...formData, structuredSkills: updatedSkills });
                                }}
                                className="w-full px-2 py-1.5 text-xs border border-gray-200 dark:border-primary-900/30 rounded-lg bg-transparent focus:ring-2 focus:ring-primary-800 dark:text-white font-medium"
                              >
                                <option value="true">Must-Have</option>
                                <option value="false">Nice-to-Have</option>
                              </select>
                            </td>
                            <td className="px-4 py-2 text-right">
                              {confirmDeleteSkillIdx === idx ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <span className="text-[11px] font-semibold text-red-500 whitespace-nowrap">Remove?</span>
                                  <button
                                    type="button"
                                    onClick={() => { removeStructuredSkill(idx); setConfirmDeleteSkillIdx(null); }}
                                    className="px-2 py-0.5 text-[11px] font-bold bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
                                  >Yes</button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDeleteSkillIdx(null)}
                                    className="px-2 py-0.5 text-[11px] font-bold bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-600 dark:text-slate-200 rounded-md transition-colors"
                                  >No</button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteSkillIdx(idx)}
                                  className="text-gray-400 hover:text-red-500 transition-colors p-1 rounded"
                                  title="Remove skill"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 px-8 py-5 border-t border-gray-100 dark:border-primary-900/30 bg-gray-50/50 dark:bg-[#001c3d]/45 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#1a3884] hover:bg-[#122a6b] text-white font-bold rounded-xl shadow-md shadow-[#1a3884]/20 border-none transition-all"
            >
              {submitting ? (job ? 'Saving...' : 'Posting...') : (job ? 'Save Changes' : 'Create Listing')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const JobFairModal = ({ isOpen, onClose, fair, companies = [], recruiters = [], onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    location: '',
    status: 'active',
    companies: [],
    registrationDeadline: '',
    bannerImage: '',
    isOpenToAll: true,
    allowedDegrees: '',
    allowedBranches: ''
  });
  const [uploading, setUploading] = useState(false);
  const bannerInputRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const [isCompaniesDropdownOpen, setIsCompaniesDropdownOpen] = useState(false);
  const [companySearch, setCompanySearch] = useState('');

  // Event-level eligibility, sourced from the shared degree catalog.
  const { getDegreeOptions, getDegreeLabel, getSpecializationsForDegrees } = useDegrees();
  const [showFairDegreeDropdown, setShowFairDegreeDropdown] = useState(false);
  const [showFairBranchDropdown, setShowFairBranchDropdown] = useState(false);
  const [fairDegreeSearch, setFairDegreeSearch] = useState('');
  const [fairBranchSearch, setFairBranchSearch] = useState('');

  const fairQualifications = getDegreeOptions();
  const fairSelectedDegrees = formData.allowedDegrees
    ? formData.allowedDegrees.split(',').map(d => d.trim()).filter(Boolean)
    : [];
  const fairSelectedBranches = formData.allowedBranches
    ? formData.allowedBranches.split(',').map(b => b.trim()).filter(Boolean)
    : [];
  const fairBranches = getSpecializationsForDegrees(fairSelectedDegrees);

  useEffect(() => {
    if (fair) {
      setFormData({
        title: fair.title || '',
        description: fair.description || '',
        startDate: fair.startDate ? new Date(fair.startDate).toISOString().split('T')[0] : '',
        endDate: fair.endDate ? new Date(fair.endDate).toISOString().split('T')[0] : '',
        location: fair.location || '',
        mode: fair.mode || 'offline',
        status: fair.status || 'draft',
        companies: fair.companies ? fair.companies.map(c => c._id || c) : [],
        registrationDeadline: fair.registrationDeadline ? new Date(fair.registrationDeadline).toISOString().split('T')[0] : '',
        bannerImage: fair.bannerImage || '',
        isOpenToAll: fair.eligibility?.isOpenToAll !== false,
        allowedDegrees: (fair.eligibility?.allowedDegrees || []).join(', '),
        allowedBranches: (fair.eligibility?.allowedBranches || []).join(', ')
      });
    } else {
      setFormData({
        title: '',
        description: '',
        startDate: '',
        endDate: '',
        location: '',
        mode: 'offline',
        status: 'active',
        companies: [],
        registrationDeadline: '',
        bannerImage: '',
        isOpenToAll: true,
        allowedDegrees: '',
        allowedBranches: ''
      });
    }
  }, [fair, isOpen]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('image', file);

    try {
      const { data } = await api.post('/upload/image', uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data.success) {
        setFormData(prev => ({ ...prev, bannerImage: data.data.url }));
        toast.success('Banner image uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.startDate || !formData.endDate || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.companies || formData.companies.length === 0) {
      toast.error('Please select at least one Participating Hiring Partner / Company');
      return;
    }

    const payload = {
      title: formData.title,
      description: formData.description,
      startDate: formData.startDate,
      endDate: formData.endDate,
      location: formData.location,
      mode: formData.mode,
      status: formData.status,
      companies: formData.companies,
      recruiters: fair && fair.recruiters ? fair.recruiters.map(r => r._id || r) : [],
      registrationDeadline: formData.registrationDeadline || null,
      bannerImage: formData.bannerImage || '',
      eligibility: {
        isOpenToAll: !!formData.isOpenToAll,
        allowedDegrees: formData.isOpenToAll ? [] : fairSelectedDegrees,
        allowedBranches: formData.isOpenToAll ? [] : fairSelectedBranches
      }
    };

    try {
      if (fair) {
        await api.put(`/placements/job-fairs/${fair._id}`, payload);
        toast.success('Job Fair updated successfully!');
      } else {
        await api.post('/placements/job-fairs', payload);
        toast.success('Job Fair created successfully!');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save Job Fair');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in lg:left-64" onClick={onClose}>
      <div className="relative bg-white dark:bg-[#001e42] w-full max-w-3xl rounded-3xl shadow-[0_32px_80px_rgba(0,0,0,0.4)] overflow-hidden border border-gray-100 dark:border-[#1a3884]/40 flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 dark:border-[#1a3884]/30 bg-white dark:bg-[#001430] shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-2xl bg-[#1a3884] shadow-lg text-white shrink-0">
              <Plus className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white tracking-tight leading-tight">
                {fair ? 'Modify Job Fair Event' : 'Create Job Fair Event'}
              </h2>
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5 font-medium">
                {fair ? 'Update the details for this job fair event' : 'Host a new hiring event with partners'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 rounded-xl transition-colors text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0 bg-transparent">
          <div className="px-8 py-6 space-y-6 overflow-y-auto max-h-[calc(90vh-160px)] custom-scrollbar">
            {/* Row 1: Title & Status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Event Title <span className="text-[#1a3884]">*</span>
                </label>
                <input
                  type="text"
                  placeholder="E.g. Annual Tech Career Expo 2026"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Event Status <span className="text-[#1a3884]">*</span>
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all appearance-none"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active (Live)</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Description <span className="text-[#1a3884]">*</span>
              </label>
              <textarea
                placeholder="Details about the job fair..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                className="w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 transition-all resize-none leading-relaxed"
              />
            </div>

            {/* Row 2: Event Mode & Venue */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Event Mode <span className="text-[#1a3884]">*</span>
                </label>
                <select
                  value={formData.mode}
                  onChange={(e) => setFormData({ ...formData, mode: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all appearance-none"
                >
                  <option value="offline">Offline / In-Person</option>
                  <option value="online">Online / Virtual</option>
                  <option value="hybrid">Hybrid</option>
                </select>
              </div>

              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Venue / Meeting Link <span className="text-[#1a3884]">*</span>
                </label>
                <input
                  type="text"
                  placeholder={formData.mode === 'online' ? "E.g. Zoom Link" : "E.g. Main Auditorium"}
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-600 transition-all"
                />
              </div>
            </div>

            {/* Row 3: Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Registration RSVP Deadline
                </label>
                <input
                  type="date"
                  value={formData.registrationDeadline}
                  onChange={(e) => setFormData({ ...formData, registrationDeadline: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  Start Date <span className="text-[#1a3884]">*</span>
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                  End Date <span className="text-[#1a3884]">*</span>
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white transition-all"
                />
              </div>
            </div>

            {/* Row 4: Participating Companies */}
            <div className="space-y-1.5 relative" ref={dropdownRef}>
              <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Participating Hiring Partners / Companies <span className="text-[#1a3884]">*</span>
              </label>
              <button
                type="button"
                onClick={() => setIsCompaniesDropdownOpen(!isCompaniesDropdownOpen)}
                className="w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#1a3884] focus:border-transparent bg-gray-50 dark:bg-[#001430] text-sm font-medium text-gray-900 dark:text-white flex items-center justify-between cursor-pointer hover:border-[#1a3884]/40 transition-all"
              >
                <span className="truncate">
                  {formData.companies.length > 0 
                    ? <span className="font-bold text-[#1a3884] dark:text-[#4f7dff]">{formData.companies.length} compan{formData.companies.length > 1 ? 'ies' : 'y'} selected</span>
                    : <span className="text-gray-400 dark:text-slate-500">Select participating companies...</span>}
                </span>
                <div className={`transition-transform duration-200 text-gray-400 ${isCompaniesDropdownOpen ? 'rotate-180' : ''}`}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
                </div>
              </button>

              {isCompaniesDropdownOpen && (
                <div className="absolute z-[100] mt-1 w-full bg-white dark:bg-[#001f40] border border-gray-200 dark:border-[#1a3884]/40 rounded-xl shadow-xl max-h-72 flex flex-col py-1.5 top-full overflow-hidden">
                  <div className="px-3 py-2 border-b border-gray-100 dark:border-[#1a3884]/30 shrink-0 sticky top-0 bg-white dark:bg-[#001f40] z-10">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input 
                        type="text" 
                        placeholder="Search companies..." 
                        value={companySearch}
                        onChange={(e) => setCompanySearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-[#001430] border border-gray-200 dark:border-[#1a3884]/30 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#1a3884] dark:text-white"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="overflow-y-auto custom-scrollbar flex-1">
                  {companies.filter(c => c.college && c.name.toLowerCase().includes(companySearch.toLowerCase())).map(c => {
                    const isSelected = formData.companies.includes(c._id);
                    return (
                      <button
                        type="button"
                        key={c._id}
                        onClick={() => {
                          const newCompanies = isSelected
                            ? formData.companies.filter(id => id !== c._id)
                            : [...formData.companies, c._id];
                          setFormData({ ...formData, companies: newCompanies });
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-xs font-semibold transition-colors text-left ${isSelected
                          ? 'bg-[#1a3884]/5 dark:bg-[#4f7dff]/10 text-[#1a3884] dark:text-[#4f7dff]'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-[#1a3884]/5 dark:hover:bg-[#4f7dff]/10'
                          }`}
                      >
                        <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-[#1a3884] border-[#1a3884]' : 'bg-white dark:bg-[#00152e] border border-gray-300 dark:border-slate-600'}`}>
                          {isSelected && <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>}
                        </div>
                        <span className="truncate">{c.name}</span>
                      </button>
                    );
                  })}
                  {companies.filter(c => c.college && c.name.toLowerCase().includes(companySearch.toLowerCase())).length === 0 && (
                    <div className="px-4 py-4 text-center text-xs text-gray-500">
                      No companies found
                    </div>
                  )}
                  </div>
                </div>
              )}
            </div>

            {/* Student Eligibility — degrees & specialisations from the shared catalog */}
            <div className="space-y-4 pt-2 border-t border-gray-100 dark:border-[#1a3884]/30">
              <div className="flex items-center justify-between pt-4">
                <div>
                  <h4 className="text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Student Eligibility
                  </h4>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5 font-medium">
                    Controls who can register for this fair. Individual job postings can narrow it further.
                  </p>
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none shrink-0">
                  <input
                    type="checkbox"
                    checked={!!formData.isOpenToAll}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        isOpenToAll: e.target.checked,
                        allowedDegrees: e.target.checked ? '' : prev.allowedDegrees,
                        allowedBranches: e.target.checked ? '' : prev.allowedBranches
                      }));
                      if (e.target.checked) { setShowFairDegreeDropdown(false); setShowFairBranchDropdown(false); }
                    }}
                    className="w-4 h-4 rounded text-[#1a3884] focus:ring-[#1a3884] dark:bg-slate-900 dark:border-slate-700 cursor-pointer"
                  />
                  <span className="text-xs font-semibold text-gray-600 dark:text-slate-300 uppercase tracking-wider">Open to all</span>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Eligible degrees */}
                <div className="relative space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Eligible Degrees
                    </label>
                    {fairSelectedDegrees.length > 0 && !formData.isOpenToAll && (
                      <span className="text-[11px] font-semibold text-[#1a3884] dark:text-blue-400">
                        {fairSelectedDegrees.length} selected
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    disabled={formData.isOpenToAll}
                    onClick={() => setShowFairDegreeDropdown(!showFairDegreeDropdown)}
                    className={`w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl bg-gray-50 dark:bg-[#001430] text-sm font-medium dark:text-white flex items-center justify-between transition-all ${formData.isOpenToAll ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-[#1a3884]/50'}`}
                  >
                    <span className="truncate pr-4 text-left text-sm">
                      {formData.isOpenToAll
                        ? 'All degrees'
                        : (fairSelectedDegrees.map(getDegreeLabel).join(', ') || 'Select degrees...')}
                    </span>
                    <ChevronDown size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  </button>
                  {!formData.isOpenToAll && showFairDegreeDropdown && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowFairDegreeDropdown(false)} />
                      <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-[#001f40] border border-gray-200 dark:border-[#1a3884]/40 rounded-xl shadow-xl py-2 z-50 max-h-64 overflow-y-auto custom-scrollbar flex flex-col">
                        <div className="px-3 pb-2 border-b border-gray-100 dark:border-[#1a3884]/20">
                          <input
                            type="text"
                            value={fairDegreeSearch}
                            onChange={(e) => setFairDegreeSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            placeholder="Search degree..."
                            className="w-full px-3 py-1.5 border border-gray-300 dark:border-slate-700 rounded-lg bg-transparent text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-[#1a3884]"
                          />
                        </div>
                        <div className="overflow-y-auto max-h-44 mt-1">
                          {fairQualifications
                            .filter(o => o.label.toLowerCase().includes(fairDegreeSearch.toLowerCase()))
                            .map((opt) => {
                              const qual = opt.value;
                              const isSelected = fairSelectedDegrees.includes(qual);
                              return (
                                <label key={qual} className="flex items-center gap-3 px-4 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      const updated = isSelected
                                        ? fairSelectedDegrees.filter(i => i !== qual)
                                        : [...fairSelectedDegrees, qual];
                                      // Keep only branches the new degree set still offers.
                                      const stillValid = getSpecializationsForDegrees(updated);
                                      setFormData(prev => ({
                                        ...prev,
                                        allowedDegrees: updated.join(', '),
                                        allowedBranches: fairSelectedBranches.filter(b => stillValid.includes(b)).join(', ')
                                      }));
                                    }}
                                    className="w-4 h-4 rounded text-[#1a3884] focus:ring-[#1a3884] dark:bg-slate-900 dark:border-slate-700"
                                  />
                                  <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{opt.label}</span>
                                </label>
                              );
                            })}
                          {fairQualifications.filter(o => o.label.toLowerCase().includes(fairDegreeSearch.toLowerCase())).length === 0 && (
                            <div className="text-center py-4 text-xs text-gray-500">No degrees found</div>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Eligible specialisations dropdown */}
                <div className="relative space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                      Eligible Specialisations
                      {!formData.isOpenToAll && fairSelectedDegrees.length > 0 && (
                        <span className="ml-1.5 normal-case tracking-normal font-semibold text-[#1a3884] dark:text-blue-400">
                          ({fairBranches.length} for {fairSelectedDegrees.map(q => getDegreeLabel(q).split(' (')[0]).join(', ')})
                        </span>
                      )}
                    </label>
                    {formData.allowedBranches && !formData.isOpenToAll && fairSelectedDegrees.length > 0 && (
                      <span className="text-[11px] font-semibold text-[#1a3884] dark:text-blue-400">
                        {fairSelectedBranches.length} selected
                      </span>
                    )}
                  </div>
                  {(() => {
                    const isFairBranchDisabled = formData.isOpenToAll || fairSelectedDegrees.length === 0;
                    return (
                      <>
                        <button
                          type="button"
                          disabled={isFairBranchDisabled}
                          onClick={() => setShowFairBranchDropdown(!showFairBranchDropdown)}
                          className={`w-full px-4 py-3 border border-gray-200 dark:border-[#1a3884]/30 rounded-xl bg-gray-50 dark:bg-[#001430] text-sm font-medium dark:text-white flex items-center justify-between transition-all
                              ${isFairBranchDisabled ? 'opacity-50 cursor-not-allowed bg-gray-100 dark:bg-slate-900/40 text-gray-400' : 'cursor-pointer hover:border-[#1a3884]/50'}`}
                        >
                          <span className="truncate pr-4 text-left text-sm">
                            {formData.isOpenToAll
                              ? 'All specialisations'
                              : fairSelectedDegrees.length === 0
                              ? 'Select degree first'
                              : (formData.allowedBranches || 'Select specialisations...')
                            }
                          </span>
                          <ChevronDown size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
                        </button>
                        {!isFairBranchDisabled && showFairBranchDropdown && (
                          <>
                            <div className="fixed inset-0 z-40" onClick={() => setShowFairBranchDropdown(false)} />
                            <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-[#001f40] border border-gray-200 dark:border-[#1a3884]/40 rounded-xl shadow-xl py-2 z-50 max-h-64 overflow-y-auto custom-scrollbar flex flex-col">
                              <div className="px-3 pb-2 border-b border-gray-100 dark:border-[#1a3884]/20">
                                <input
                                  type="text"
                                  value={fairBranchSearch}
                                  onChange={(e) => setFairBranchSearch(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  placeholder="Search specialisation..."
                                  className="w-full px-3 py-1.5 border border-gray-300 dark:border-slate-700 rounded-lg bg-transparent text-xs dark:text-white focus:outline-none focus:ring-1 focus:ring-[#1a3884]"
                                />
                              </div>
                              {/* Select All Fair Specialisations Bar */}
                              <div className="px-3 py-1.5 border-b border-gray-100 dark:border-[#1a3884]/20 flex items-center justify-between bg-gray-50/70 dark:bg-[#001430]/70">
                                {(() => {
                                  const filteredBranches = fairBranches.filter(b => b.toLowerCase().includes(fairBranchSearch.toLowerCase()));
                                  const allFilteredSelected = filteredBranches.length > 0 && filteredBranches.every(b => fairSelectedBranches.includes(b));
                                  const someFilteredSelected = filteredBranches.some(b => fairSelectedBranches.includes(b));
                                  return (
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                      <input
                                        type="checkbox"
                                        checked={allFilteredSelected}
                                        ref={input => {
                                          if (input) input.indeterminate = !allFilteredSelected && someFilteredSelected;
                                        }}
                                        onChange={() => {
                                          if (allFilteredSelected) {
                                            const updated = fairSelectedBranches.filter(b => !filteredBranches.includes(b));
                                            setFormData(prev => ({ ...prev, allowedBranches: updated.join(', ') }));
                                          } else {
                                            const newSelected = Array.from(new Set([...fairSelectedBranches, ...filteredBranches]));
                                            setFormData(prev => ({ ...prev, allowedBranches: newSelected.join(', ') }));
                                          }
                                        }}
                                        className="w-4 h-4 rounded text-[#1a3884] focus:ring-[#1a3884] dark:bg-slate-900 dark:border-slate-700 cursor-pointer"
                                      />
                                      <span className="text-xs font-bold text-[#1a3884] dark:text-blue-400">
                                        {allFilteredSelected ? 'Deselect All' : 'Select All'} ({filteredBranches.length})
                                      </span>
                                    </label>
                                  );
                                })()}
                                {formData.allowedBranches && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setFormData(prev => ({ ...prev, allowedBranches: '' }));
                                    }}
                                    className="text-[11px] font-semibold text-rose-500 hover:text-rose-700"
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                              <div className="overflow-y-auto max-h-44 mt-1">
                                {fairBranches
                                  .filter(b => b.toLowerCase().includes(fairBranchSearch.toLowerCase()))
                                  .map((branch) => {
                                    const isSelected = fairSelectedBranches.includes(branch);
                                    return (
                                      <label key={branch} className="flex items-center gap-3 px-4 py-1.5 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer select-none">
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() => {
                                            const updated = isSelected
                                              ? fairSelectedBranches.filter(i => i !== branch)
                                              : [...fairSelectedBranches, branch];
                                            setFormData(prev => ({ ...prev, allowedBranches: updated.join(', ') }));
                                          }}
                                          className="w-4 h-4 rounded text-[#1a3884] focus:ring-[#1a3884] dark:bg-slate-900 dark:border-slate-700"
                                        />
                                        <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">{branch}</span>
                                      </label>
                                    );
                                  })}
                                {fairBranches.filter(b => b.toLowerCase().includes(fairBranchSearch.toLowerCase())).length === 0 && (
                                  <div className="text-center py-4 text-xs text-gray-500">No specialisations found</div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Row 5: Banner Image (Not Mandatory) */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                Event Banner Image
              </label>
              <input
                type="file"
                ref={bannerInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                className="hidden"
              />
              {formData.bannerImage ? (
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#001430] border border-gray-200 dark:border-[#1a3884]/30 rounded-xl">
                  <div className="flex items-center gap-2.5 truncate">
                    <img
                      src={formData.bannerImage.startsWith('http') ? formData.bannerImage : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${formData.bannerImage}`}
                      alt="Banner Preview"
                      className="w-10 h-10 object-cover rounded-lg border border-gray-200 dark:border-[#1a3884]/30"
                    />
                    <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 truncate">
                      Banner Uploaded
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, bannerImage: '' }))}
                    className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => !uploading && bannerInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 dark:border-[#1a3884]/40 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 dark:hover:bg-[#1a3884]/5 transition-all"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-6 h-6 text-[#1a3884] animate-spin mb-2" />
                      <span className="text-sm font-semibold text-gray-600 dark:text-slate-400">Uploading banner...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-gray-400 mb-2" />
                      <span className="text-sm font-semibold text-[#1a3884] dark:text-[#4f7dff]">Upload Banner Image</span>
                      <span className="text-xs text-gray-400 dark:text-slate-500 mt-1">Supports PNG, JPG, JPEG up to 5MB (Optional)</span>
                    </>
                  )}
                </div>
              )}
            </div>

          </div>

          {/* Footer Actions */}
          <div className="flex gap-3 items-center justify-end px-8 py-5 border-t border-gray-100 dark:border-[#1a3884]/30 bg-gray-50/50 dark:bg-[#001c3d]/45 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#1a3884] hover:bg-[#0f225c] text-white text-sm font-bold rounded-xl shadow-md shadow-[#1a3884]/20 transition-all active:scale-95"
            >
              <Save className="w-4 h-4" />
              {fair ? 'Save Changes' : 'Create Event'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CustomDropdown = ({ value, onChange, options, placeholder, showSearch = false, direction = 'down' }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const dropdownRef = React.useRef(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  React.useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  React.useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);
  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex w-full items-center justify-between gap-3 rounded-[1.25rem] border px-4 py-3.5 text-left shadow-sm transition-all ${isOpen
          ? 'border-[var(--placement-primary)] ring-4 ring-[var(--placement-primary-border)]/12 shadow-lg shadow-[var(--placement-primary)]/10 dark:shadow-[#001030]/50 ' + (isDark ? 'bg-[#002147]' : 'bg-white')
          : 'border-slate-200 dark:border-[var(--placement-primary-border)]/30 bg-slate-50/70 dark:bg-slate-900/40 hover:border-[var(--placement-primary)]/40 ' + (isDark ? 'hover:bg-[#002147]' : 'hover:bg-white')
          }`}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className={selectedOption ? 'truncate pr-4 text-[14px] font-semibold text-slate-800 dark:text-slate-100' : 'truncate pr-4 text-[14px] font-medium text-slate-400 dark:text-slate-500'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDown className={`h-4 w-4 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180 text-[var(--placement-primary)]' : 'text-slate-400 dark:text-slate-500'}`} />
      </button>

      {isOpen && (
        <div className={`absolute left-0 right-0 z-[110] p-2 shadow-2xl border border-slate-200 dark:border-[var(--placement-primary-border)]/40 rounded-[1.25rem] backdrop-blur-md animate-fade-in-up flex flex-col gap-1.5 ${direction === 'up'
          ? 'bottom-full mb-2'
          : 'top-full mt-2'
          } ${isDark ? 'bg-[#002147]/95' : 'bg-white/95'}`}>
          {showSearch && (
            <div className="relative px-2 py-1 flex-shrink-0">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-9 pr-4 py-2 text-sm rounded-xl border font-semibold transition-all outline-none ${isDark
                  ? 'bg-slate-950 border-[var(--placement-primary-border)]/40 text-white focus:border-[var(--placement-primary)]/60'
                  : 'bg-slate-50 border-slate-200 text-slate-800 focus:border-[var(--placement-primary)]/60'
                  }`}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="max-h-48 overflow-y-auto custom-scrollbar flex-1 p-1">
            {filteredOptions.length === 0 && (
              <div className="px-3 py-3 text-sm text-slate-400 dark:text-slate-500 font-medium">
                No matching options
              </div>
            )}
            {filteredOptions.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  type="button"
                  key={option.value}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  className={`w-full rounded-xl border border-transparent px-4 py-2.5 text-left text-[14px] font-semibold transition-all flex items-center justify-between ${isSelected
                    ? 'bg-[var(--placement-primary)]/12 text-[#1a3673] dark:text-white dark:bg-[var(--placement-primary)]/20'
                    : 'text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[var(--placement-primary-hover)]/10 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  role="option"
                  aria-selected={isSelected}
                >
                  <span className="block leading-5 truncate pr-2">{option.label}</span>
                  {isSelected && (
                    <CheckCircle2 className="w-4 h-4 text-[var(--placement-primary)] flex-shrink-0 ml-2" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const EnrollmentModal = ({ isOpen, onClose, jobs, onSuccess }) => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    studentId: '', jobId: '', status: 'placed'
  });

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const { data } = await api.get('/users?role=student');
        setStudents(data.data || []);
      } catch (error) {
        console.error('Error fetching students:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) fetchStudents();
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.studentId || !formData.jobId) return toast.error('Selection criteria incomplete');

    try {
      await api.post('/placements/applications/enroll', formData);
      toast.success('Student career milestone recorded successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Placement logging failed');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in lg:left-64" onClick={onClose}>
      <div className="relative bg-white dark:bg-[#00152E] rounded-[1.5rem] shadow-2xl flex flex-col w-full max-w-[480px] overflow-hidden border border-slate-100 dark:border-[var(--placement-primary-border)]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-slate-800 flex-shrink-0 bg-white dark:bg-[#00152E]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[var(--placement-primary-light)] dark:bg-[var(--placement-primary-light)] rounded-xl flex items-center justify-center shrink-0 text-[var(--placement-primary)] dark:text-[var(--placement-primary)]">
              <GradCap className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">
                Log Career Milestone
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Record student placements and job advancement stages
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-655 dark:hover:text-white rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden bg-white dark:bg-[#00152E]">
          <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-160px)] custom-scrollbar">
            {/* Target Student */}
            <div className="space-y-2">
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                Target Student *
              </label>
              <CustomDropdown
                value={formData.studentId}
                onChange={(val) => setFormData({ ...formData, studentId: val })}
                placeholder="Search and select a student"
                showSearch={true}
                options={students.map(s => ({
                  value: s._id,
                  label: `${s.fullName} — ${s.studentId || s.rollNumber || 'No ID'}`
                }))}
              />
            </div>

            {/* Assign Opportunity */}
            <div className="space-y-2">
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                Assign Opportunity *
              </label>
              <CustomDropdown
                value={formData.jobId}
                onChange={(val) => setFormData({ ...formData, jobId: val })}
                placeholder="Link an active recruitment drive"
                showSearch={true}
                options={jobs.map(j => ({
                  value: j._id,
                  label: `${j.title} @ ${j.company?.name}`
                }))}
              />
            </div>

            {/* Advancement Stage */}
            <div className="space-y-2">
              <label className="block text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-0.5">
                Advancement Stage *
              </label>
              <CustomDropdown
                value={formData.status}
                onChange={(val) => setFormData({ ...formData, status: val })}
                placeholder="Select current stage"
                direction="up"
                options={[
                  { value: 'applied', label: 'Application Submitted' },
                  { value: 'shortlisted', label: 'Shortlisted' },
                  { value: 'interviewing', label: 'Interview in Progress' },
                  { value: 'offered', label: 'Offer Received' },
                  { value: 'placed', label: 'Placement Finalized' },
                  { value: 'rejected', label: 'Not Selected' }
                ]}
              />
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex gap-4 items-center justify-end px-6 py-5 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-[#001f40]/40 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 bg-white dark:bg-[#002147] border border-slate-200/60 dark:border-slate-700/50 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-semibold hover:bg-slate-50 dark:hover:bg-[var(--placement-primary-hover)]/20 transition-all shadow-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-[var(--placement-primary)] hover:bg-[var(--placement-primary-hover)] text-white rounded-xl text-sm font-semibold flex items-center gap-2 transition-all shadow-md shadow-[var(--placement-primary-border)]"
            >
              <GradCap className="w-4 h-4" />
              Commit Success
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlacementOffice;



